'use client';
// Tableau de bord — demandes à statuer, révisions à échéance, retraits actifs (les chiffres du fonds sont dans Documents).
// (Le calendrier du laboratoire arrive avec la phase G3.)
import { attendDeMoi, signatureBloquee } from '@/lib/taches-signature';
import { etapesAMoi, type EtapeMoi } from '@/lib/mes-documents';
import React from 'react';
import { useGed, userName } from '@/lib/store';
import { pastilleDe } from '@/lib/couleurs-personne';
import { droitsNav } from '@/lib/navigation';
import { fmtDate, vocabMeta, graviteMeta, NC_STATES, ACTION_STATES, REC_STATES, REC_CANAUX, AUDIT_STATES, jourIso, aujourdhuiIso } from '@/lib/meta';
import { EmptyState, TitrePage, StatePill, I, card, Skeleton } from '@/components/ui';
import { MonJour } from '@/components/MonJour';
/* EXPRESSO — la veille biologie du jour (RQ, 05/10/2026). Ne rend rien quand
   il n'y a rien À DIRE : voir l'en-tête du composant pour les deux vides. */
import { Expresso } from '@/components/Expresso';
import { RappelsFermeture } from '@/components/RappelsFermeture';
// GED-12 — la création d'une revue depuis le calendrier réutilise LE formulaire
// de planification, pas une copie. Voir l'en-tête de `PlanifierRevue.tsx`.
import { Modale } from '@/components/ui';
import type { MaintenanceOccurrence, MaintenanceLigne } from '@/lib/types';
// Le formulaire de ligne du registre, réutilisé TEL QUEL depuis l'agenda : en
// réécrire un second ici aurait fait deux formulaires pour un même objet, dont
// l'un aurait cessé de suivre l'autre à la première évolution.
import { FormulaireLigne, brouillonDe, Acquitter } from './ressources/Maintenances';
// « Ma vue » ne réinvente aucune règle : elle réutilise les prédicats des
// registres eux-mêmes, sinon les deux écrans divergent au premier changement.
import { isLate, actionOuverte, actionMienne } from './Actions';
import { ncOuverte, ncSoumise, peutTrancherNC } from './NC';
/* QUITTER un signalement lu pour information (RQ, 05/10/2026) — la règle
   « nouveau » et la fusion des deux registres sont un module PUR, éprouvé par
   `npm test` : « nouveau » se calcule à chaque affichage et ne se stocke
   jamais (un dossier figé « new » le resterait). */
import {
  pourLeTableauDeBord, fondre, dateDuSignalement, lusPourMoi, JOURS_NOUVEAUTE,
  type SignalementLisible, type NatureSignalement,
} from '@/lib/signalements-lus';
import { reclOuverte } from './Reclamations';
import { auditOuvert, auditEnRetard } from './Audits';
// Accès rapides (17/08/2026) — quatre raccourcis fixes pour les gestes
// quotidiens des techniciens, suivis des favoris personnels de chacun.
import { AccesRapides } from '@/components/AccesRapides';

import { Bouton } from '@/shared/Bouton';
import { useOccurrences } from '@/lib/conventions';
import { COULEUR_FERIE, libelleCourtFerie } from '@/lib/feries';
import { libelleMois, libelleJourLong } from '@/lib/jour';
/** Les familles d'événements du calendrier — l'ordre est celui du panneau de
 *  filtre, et il va du plus consulté au plus rare. */
type Famille = 'planning' | 'conventions' | 'feries' | 'qualite' | 'maintenances' | 'metro' | 'actions' | 'audits' | 'revues' | 'rdd' | 'revuetiers';
const FAMILLES: { cle: Famille; libelle: string; couleur: string }[] = [
  /* ── LE CALENDRIER DE TOUT LE LABORATOIRE (RQ, 12/10/2026) ──────────────
     « Il s'appellera Calendrier et regroupera tous les événements du labo
     (planning, conventions, RDD, CQE, dates limites…). » Quatre couches
     rejoignent les sept qui existaient : mes affectations, les conventions et
     événements des contacts (des points discrets jusqu'ici), les jours fériés,
     et les échéances de la qualité analytique (CQ, EEQ, péremptions) — lues
     aux routes qui les calculent déjà, jamais recalculées ici. */
  { cle: 'planning', libelle: 'Mon planning', couleur: 'var(--cyan-700)' },
  { cle: 'conventions', libelle: 'Conventions & événements des contacts', couleur: 'var(--violet-600)' },
  { cle: 'feries', libelle: 'Jours fériés', couleur: 'var(--amber-600)' },
  { cle: 'qualite', libelle: 'Échéances qualité (CQ, EEQ, péremptions)', couleur: 'var(--blue-700)' },
  { cle: 'maintenances', libelle: 'Maintenances préventives', couleur: 'var(--green-600)' },
  { cle: 'metro', libelle: 'Échéances métrologie', couleur: 'var(--red-500)' },
  { cle: 'actions', libelle: 'Actions à échéance', couleur: 'var(--amber-500)' },
  { cle: 'audits', libelle: 'Audits planifiés', couleur: 'var(--blue-600)' },
  { cle: 'revues', libelle: 'Revues de documents', couleur: 'var(--cyan-600)' },
  { cle: 'rdd', libelle: 'Revues de direction', couleur: 'var(--navy-800)' },
  /* Revues de contrat des tiers (L2, 10/10/2026) : la prochaine revue CALCULÉE
     par le moteur, jamais une date saisie. */
  { cle: 'revuetiers', libelle: 'Revues de contrat (tiers)', couleur: 'var(--violet-600)' },
];
/* ⚠️ LA CLÉ MÉMORISE LES FAMILLES MASQUÉES, plus les affichées (12/10/2026).
   L'ancienne retenait les cochées : une famille AJOUTÉE au code y manquait, donc
   restait masquée chez quiconque avait un jour réglé le filtre — sans rien qui
   le dise. L'ancienne clé se relit une fois, contre la liste de l'époque. */
const CLE_FILTRE = 'bio24.calendrier.masquees';
const CLE_FILTRE_ANCIENNE = 'bio24.calendrier.familles';
const FAMILLES_ANCIENNES: Famille[] = ['maintenances', 'metro', 'actions', 'audits', 'revues', 'rdd', 'revuetiers'];

/** Calendrier du laboratoire — agrège revues documentaires, échéances
 *  d'actions, audits planifiés, revues de direction et échéances métrologie. */
/** Événements montrés par case en vue Mois avant « +N » — 2 pour un calendrier compact (RQ, 04/10/2026). */
const PAR_CASE_MOIS = 2;

/** Entrées montrées par rubrique de « Ma vue » avant « Voir les N » (RQ, 04/10/2026). */
const MAX_MA_VUE = 7;

export function LabCalendar({ plein = false }: { plein?: boolean } = {}) {
  // `equips` n'est plus lu ici : depuis le lot EQP-1, l'échéance de métrologie
  // vient du parc SERVI PAR le registre de maintenance, avec sa date dérivée.
  const { docs, actions, audits, rdds, maintenances, loadMaintenances, session, settings, go, ouvrirPanneau, openDoc, naviguerVers, rhAcces } = useGed();
  const isRQ = session.roles.includes('rq');
  /* LES REVUES DE CONTRAT DUES (L2, 10/10/2026) — lues au moteur, qui calcule la
     prochaine échéance ; le calendrier n'en recalcule aucune. Seules celles qui
     ont une date (périodicité ET point de départ) s'affichent : un tiers « jamais
     revu » n'a pas d'échéance à une date quelconque. Au RQ et aux achats. */
  const [revuesTiers, setRevuesTiers] = React.useState<{ contactCode: string; nom: string; prochaine: { date: string; etat: string } }[]>([]);
  React.useEffect(() => {
    if (!isRQ && !session.roles.includes('achats')) return undefined;
    let vivant = true;
    void fetch('/api/ged/ged/revues-tiers', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j?.lignes) setRevuesTiers(j.lignes.filter((l: { actif: boolean }) => l.actif)); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [isRQ, session.roles]);
  /* ⚠️ TENIR LE PLAN N'EST PAS DÉCLARER FAITE (RQ, 05/10/2026) : « je ne veux
     pas que Lamia ait les droits de modifier une maintenance. Pas pour le
     profil de base. »

     Le registre gardait déjà ses écritures (`isRQ`) ; CETTE fenêtre-ci, non —
     « Modifier cette maintenance » s'y offrait à tout le monde, et c'est par
     l'agenda qu'on arrive sur une maintenance. On lit le MÊME droit que la
     garde du serveur (`sec.maintenances.modifier`, fermée au profil de base
     depuis le 05/10) : une commande refusée n'est pas grisée, elle est
     ABSENTE. « Déclarer faite » reste, pour tout le monde : c'est l'acte de
     celui qui exécute. */
  const peutTenirLePlan = droitsNav(session, !!rhAcces?.acces).section('maintenances', 'modifier');
  // GED-12 — planifier une revue DEPUIS le calendrier. Le formulaire ouvert ici
  // est EXACTEMENT celui de l'écran « Revue de direction » : même composant,
  // même route, même objet créé. Un second formulaire propre au calendrier
  // aurait été un second chemin de création, et un second chemin diverge
  // toujours du premier — celui-ci aurait fini par créer des revues sans ordre
  // du jour, sans que rien ne le signale.
  /* LE FILTRE D'AFFICHAGE (RQ, 26/08/2026) : « je ne veux pas voir les
     maintenances », « je veux voir que les audits ». Le calendrier agrège six
     familles ; celui qui vient y chercher une échéance d'audit traverse
     quarante lignes de maintenance journalière pour la trouver.

     ⚠️ Le choix est MÉMORISÉ : un filtre à refaire à chaque visite ne se
     réutilise pas, on finit par le subir. Et il est mémorisé PAR NAVIGATEUR,
     pas en base — c'est un confort de lecture, pas un paramètre du laboratoire :
     deux personnes ne regardent pas le calendrier pour la même raison. */
  const [familles, setFamilles] = React.useState<Set<Famille>>(() => {
    const toutes = FAMILLES.map((f) => f.cle);
    if (typeof window === 'undefined') return new Set(toutes);
    try {
      const brut = window.localStorage.getItem(CLE_FILTRE);
      let masquees: string[];
      if (brut) masquees = JSON.parse(brut) as string[];
      else {
        const ancien = window.localStorage.getItem(CLE_FILTRE_ANCIENNE);
        const vues = ancien ? (JSON.parse(ancien) as string[]) : FAMILLES_ANCIENNES;
        masquees = FAMILLES_ANCIENNES.filter((f) => !vues.includes(f));
      }
      /* On ne garde que les familles CONNUES : une clé disparue du code
         laisserait sinon un filtre qui masque quelque chose d'introuvable. */
      const n = new Set(toutes.filter((f) => !masquees.includes(f)));
      return n.size ? n : new Set(toutes);
    } catch { return new Set(toutes); }
  });
  const memoriser = (n: Set<Famille>) => {
    try { window.localStorage.setItem(CLE_FILTRE, JSON.stringify(FAMILLES.map((f) => f.cle).filter((f) => !n.has(f)))); } catch { /* mode privé : le filtre marche, il ne survit pas au rechargement */ }
  };
  const [filtreOuvert, setFiltreOuvert] = React.useState(false);
  const basculerFamille = (c: Famille) => setFamilles((p) => {
    const n = new Set(p);
    if (n.has(c)) n.delete(c); else n.add(c);
    memoriser(n);
    return n;
  });
  const masquees = FAMILLES.length - familles.size;

  /* ⚠️ PLUS DE PLANIFICATION DE REVUE DEPUIS LE CALENDRIER (RQ, 26/08/2026).
     Il y avait QUATRE entrées — un bouton en barre, un « + » dans chaque case du
     mois, un dans chaque case de la semaine, un en vue jour — pour un acte
     EXCEPTIONNEL, qui se fait deux fois l'an depuis son propre onglet. Quatre
     affordances permanentes pour un geste rare, c'est de l'encombrement : elles
     occupaient la place et l'attention de ce qu'on vient réellement chercher au
     calendrier. Le formulaire, lui, n'a pas bougé : il vit dans l'onglet
     « Revue de direction ». */
  const [ym, setYm] = React.useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  /* Lot 8 (11/08/2026) — le calendrier s'affiche AUSSI par semaine et par
   * jour. La semaine commence le LUNDI, comme la grille mensuelle : c'est la
   * semaine du laboratoire, pas celle de l'ISO 8601 anglo-saxonne. `ancre`
   * porte le jour de référence des deux vues resserrées. */
  /* Par défaut le JOUR (14/08/2026), et non le mois. Ce qu'on vient chercher
     en ouvrant l'application, c'est « qu'est-ce que je dois faire aujourd'hui ».
     La grille mensuelle répond à une autre question — « comment se remplit le
     mois » — et elle reste à un clic. */
  const [vue, setVue] = React.useState<'mois' | 'semaine' | 'jour'>(plein ? 'mois' : 'jour');
  /* En pleine page (écran « Calendrier »), la case du mois a la place d'en montrer davantage. */
  const parCase = plein ? 5 : PAR_CASE_MOIS;
  const [ancre, setAncre] = React.useState(() => aujourdhuiIso());
  // Jour déplié : au-delà de 3 événements, les suivants étaient inatteignables
  // (« +2 » en simple texte).
  const [jourOuvert, setJourOuvert] = React.useState<string | null>(null);
  /** Occurrence de maintenance ouverte en surcouche depuis l'agenda. */
  const [occurrence, setOccurrence] = React.useState<MaintenanceOccurrence | null>(null);
  /** Ligne de PLAN à modifier, ouverte depuis l'occurrence (14/08/2026). */
  const [ligneAModifier, setLigneAModifier] = React.useState<MaintenanceLigne | null>(null);
  /** Occurrence à ACQUITTER, ouverte directement depuis l'agenda (19/08/2026).
   *  La personne concernée par la maintenance ne doit pas transiter par le
   *  registre pour déclarer faite une échéance qu'elle a sous les yeux. */
  const [aAcquitter, setAAcquitter] = React.useState<MaintenanceOccurrence | null>(null);
  const registreMaintenance = maintenances;
  const rechargerAgenda = React.useCallback(() => loadMaintenances(), [loadMaintenances]);
  /** Retrouver la ligne dont l'occurrence découle, et ouvrir son formulaire.
   *
   *  Si le registre chargé ne la porte pas — l'agenda peut avoir été projeté
   *  sur une fenêtre plus large que le registre en mémoire —, on le recharge
   *  une fois avant de renoncer, plutôt que d'ouvrir un formulaire vide. */
  const ouvrirModification = React.useCallback(async (planId: string) => {
    const trouver = (r: typeof maintenances) => (r?.lignes || []).find((l) => l.documentId === planId) || null;
    let ligne = trouver(maintenances);
    if (!ligne) {
      await loadMaintenances();
      ligne = trouver(useGed.getState().maintenances);
    }
    if (ligne) setLigneAModifier(ligne);
    else go('maintenances');
  }, [maintenances, loadMaintenances, go]);

  /** `heure` — plage horaire quand l'événement en porte une (les maintenances
   *  journalières en ont : « matin », « à la fermeture »). Elle sert à ORDONNER
   *  la journée : une tournée du matin avant une tournée du soir. Les
   *  événements sans heure (revue d'un document, échéance d'une action) ne s'en
   *  voient pas inventer une — ils passent après, groupés. */
  /* `assigne` et `exigeAssigne` (26/08/2026, RQ) : le calendrier disait CE
     QU'IL Y A À FAIRE sans dire PAR QUI. Une tâche sans destinataire n'est pas
     une tâche : c'est un oubli d'organisation, et il doit se voir.
     ⚠️ `exigeAssigne` est distinct de « assigné vide » : une revue de direction
     est une réunion, elle n'a pas de destinataire individuel — la peindre en
     rouge crierait au manquement là où il n'y en a pas. */
  type Ev = {
    label: string; color: string; ouvrir: () => void; heure?: string | null;
    assigne?: string | null; exigeAssigne?: boolean;
    /** Les CLÉS des personnes en charge — pour le rond coloré aux initiales. */
    cles?: string[];
    /** Le code du poste d'où vient la personne (maintenance tirée du planning). */
    poste?: string;
    /** La famille d'où vient l'événement — c'est elle que le filtre coche. */
    famille: Famille;
  };
  const events: Record<string, Ev[]> = {};
  const push = (
    famille: Famille,
    date: string | null | undefined, label: string, color: string, ouvrir: () => void,
    heure?: string | null, assigne?: string | null, exigeAssigne?: boolean,
    cles?: (string | null | undefined)[], poste?: string | null,
  ) => {
    if (!date) return;
    /* ⚠️ LE FILTRE ÉCARTE À LA SOURCE, pas à l'affichage. Poser l'événement puis
       le masquer laisserait « +3 » sur une case du mois qui n'en montre aucun,
       et les compteurs de la journée mentiraient. */
    if (!familles.has(famille)) return;
    const k = jourIso(date);
    (events[k] = events[k] || []).push({
      famille, label, color, ouvrir, heure: heure || null,
      assigne: String(assigne || '').trim() || null, exigeAssigne: exigeAssigne === true,
      cles: (cles || []).map((c) => String(c || '').trim()).filter(Boolean),
      poste: String(poste || '').trim() || undefined,
    });
  };

  /* ── QUI, EN UN ROND COLORÉ (RQ, 04/10/2026) ───────────────────────────
     Les initiales de la personne dans SA couleur — celles du planning, servies
     par le registre des maintenances (`personnes`). Une personne inconnue de
     la table (compte sans fiche, nom saisi à la main) garde un rond : initiales
     tirées du nom affiché, teinte calculée sur la clé. */
  const personnes = maintenances?.personnes || {};
  const sansPlanning = (t: string) => t.replace(/\s+—\s+planning\b.*$/i, '').trim();
  const initialesDuNom = (t: string) => sansPlanning(t).replace(/^Dr\.?\s+/i, '').split(/[\s,]+/).filter(Boolean).slice(0, 2).map((m) => m[0]).join('').toUpperCase();
  const quiDe = (e: Ev): { cle: string; initiales: string; nom: string }[] => {
    if (e.cles?.length) {
      return e.cles.map((c) => {
        const f = personnes[c];
        /* ⚠️ JAMAIS L'IDENTIFIANT : « tech.abidi » donnait « T ». À défaut de
           fiche (registre pas encore chargé, compte sans fiche), on prend le
           NOM du compte. */
        const nom = f?.nom || userName(settings, c) || c;
        return { cle: c, initiales: f?.initiales || (nom !== c ? initialesDuNom(nom) : '?'), nom };
      });
    }
    return e.assigne ? [{ cle: e.assigne, initiales: initialesDuNom(e.assigne), nom: sansPlanning(e.assigne) }] : [];
  };
  const Rond = ({ cle, initiales, nom, petit = false }: { cle: string; initiales: string; nom: string; petit?: boolean }) => {
    const p = pastilleDe(cle, personnes[cle]?.couleur);
    return (
      <span title={nom} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        minWidth: petit ? 15 : 22, height: petit ? 15 : 22, padding: '0 3px', borderRadius: 'var(--radius-pill)',
        background: p.background, color: p.color, border: `1px solid ${p.borderColor}`,
        fontFamily: 'var(--font-mono)', fontSize: petit ? 8.5 : 10.5, fontWeight: 700, lineHeight: 1 }}>
        {initiales || '?'}
      </span>
    );
  };

  /* ⚠️ LE FOND ROUGE EST DISTINCT DE LA PASTILLE ROUGE (RQ, 26/08/2026).
     « En retard » colore déjà l'événement en rouge. Si « non assigné » prenait
     la même couleur au même endroit, les deux se confondraient — et une
     maintenance à l'heure mais sans destinataire se lirait « en retard ».
     La couleur de l'événement dit sa NATURE ; le fond dit qu'il manque
     quelqu'un. Les deux peuvent être vrais en même temps, et doivent alors se
     lire tous les deux. */
  const orphelin = (e: Ev) => e.exigeAssigne === true && !e.assigne;
  const FOND_ORPHELIN = 'var(--red-50)';
  const BORD_ORPHELIN = 'var(--red-600)';

  /** Ordre d'une journée : par plage horaire, puis les sans-heure. */
  const parHeure = (a: Ev, b: Ev) => {
    const ha = String(a.heure || ''); const hb = String(b.heure || '');
    if (!ha && !hb) return 0;
    if (!ha) return 1;
    if (!hb) return -1;
    return ha.localeCompare(hb, 'fr');
  };
  // Chaque événement ouvre SON objet : `go(view)` déposait sur une liste non
  // filtrée, à charge pour l'utilisateur de retrouver ce qu'il venait de cliquer.
  //
  // Et il l'ouvre EN PANNEAU (12/08/2026) : consulter ce qui tombe un jour du
  // calendrier ne doit pas faire perdre le calendrier — on revenait sur un
  // registre, mois et vue oubliés, pour un simple coup d'œil.
  docs.forEach((d) => d.statut === 'envigueur' && push('revues', d.prochaineRevue, `Revue ${d.code}`, 'var(--cyan-600)',
    () => openDoc(d.code), null,
    (d.redacteurs || []).join(', '), true, d.redacteurs || []));
  actions.forEach((a) => !['realisee', 'verifiee'].includes(a.statut) && push('actions', a.echeance, a.ref, 'var(--amber-500)',
    () => ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: 'Tableau de bord' }), null, a.responsable, true, [a.responsable]));
  audits.forEach((a) => a.statut === 'planifie' && push('audits', a.datePlanifiee, a.ref, 'var(--blue-600)',
    () => ouvrirPanneau({ type: 'audit', valeur: a.documentId, origine: 'Tableau de bord' }), null, a.auditeur, true, [a.auditeur]));
  // La revue de direction n'a pas de panneau : elle reste une vraie navigation,
  // mais en désignant la revue à ouvrir (l'ordre `select` puis `go` faussait
  // l'historique).
  /* La revue de direction n'exige PAS d'assigné : c'est une réunion, pas une
     tâche confiée à quelqu'un. */
  rdds.forEach((r) => r.statut === 'planifiee' && push('rdd', r.dateRevue, r.ref, 'var(--navy-800)', () => naviguerVers('rdd', { cle: 'selectedRdd', valeur: r.documentId })));
  revuesTiers.forEach((l) => l.prochaine?.date && push('revuetiers', l.prochaine.date, `Revue de contrat — ${l.nom}`,
    l.prochaine.etat === 'en_retard' ? 'var(--red-600)' : 'var(--violet-600)', () => go('revuetiers')));
  // Métrologie — lot EQP-1. La date ne vient PLUS de `equips[].prochaineEcheance`,
  // qui était une colonne écrite à chaque intervention et fausse dès qu'une
  // maintenance était faite en avance, en retard, ou la périodicité changée.
  // Elle est DÉRIVÉE côté serveur (`echeanceGenerique`) et servie avec le parc
  // du registre de maintenance, à côté des occurrences du plan : une seule
  // source, un seul calcul.
  //
  // ⚠️ Les `indeterminee` ne sont volontairement PAS posées au calendrier : un
  // appareil sans périodicité n'a pas d'échéance à une date quelconque. Les y
  // mettre à une date inventée serait exactement le plan pré-rempli au jugé
  // qu'on refuse. Ils sont comptés — et nommés — dans le bandeau de couverture
  // de l'écran des maintenances.
  (maintenances?.parc || []).forEach((e) => {
    if (e.nature === 'accessoire') return; // un appareil qui ne mesure rien ne se raccorde à rien
    if (!e.echeance?.date || e.echeance.etat === 'indeterminee') return;
    push('metro', e.echeance.date, `Métrologie ${e.nom}`,
      e.echeance.etat === 'en_retard' ? 'var(--red-600)' : 'var(--red-500)',
      // Le parc porte le code d'inventaire : on ouvre L'APPAREIL en panneau,
      // au lieu de déposer sur le registre entier.
      () => ouvrirPanneau({ type: 'equip', valeur: e.code, origine: 'Tableau de bord' }),
      null, e.responsableNom, true);
  });
  // Les échéances POSÉES explicitement (rendez-vous de métrologie, terme de
  // contrat) sont un engagement pris, pas un calcul : elles s'affichent à part.
  (maintenances?.parc || []).forEach((e) => push('metro', e.echeancePosee, `Métrologie ${e.nom} (date posée)`, 'var(--red-500)',
    () => ouvrirPanneau({ type: 'equip', valeur: e.code, origine: 'Tableau de bord' }), null, e.responsableNom, true));
  // Maintenance préventive (§6.4.4, lot N4) — N4-4.
  //
  // Les DATES ne viennent d'aucune colonne : elles sont projetées par le moteur
  // à partir de la fréquence, du calendrier d'ouverture réel du laboratoire et
  // de la politique de report. Le calendrier n'en recalcule aucune — il les
  // affiche. Deux endroits qui projetteraient les mêmes échéances finiraient
  // par ne plus tomber le même jour.
  //
  // Ce qui est FAIT ne s'affiche pas : le calendrier sert à savoir ce qui reste
  // à faire. Le registre des maintenances, lui, garde tout.
  (maintenances?.occurrences || []).forEach((o) => {
    if (['faite', 'faite_tard'].includes(o.etat)) return;
    /* Désignation demandée par la RQ le 14/08/2026 : le NOM de l'appareil
       d'abord, puis la maintenance, puis sa fréquence. Le code d'inventaire
       (« EQ02 ») ne dit rien à qui lit son agenda le matin — il faut savoir de
       quelle machine il s'agit, et la fréquence distingue deux lignes qui
       portent par ailleurs le même intitulé sur le même appareil. */
    push('maintenances', o.date, `${o.equipementNom} — ${o.libelle}${o.frequenceLibelle ? ` · ${o.frequenceLibelle}` : ''}`,
      // « En retard » et « à faire » ne partagent pas la même couleur : une
      // maintenance due aujourd'hui n'est pas un manquement.
      o.etat === 'en_retard' ? 'var(--red-600)' : 'var(--green-600)',
      // Une occurrence s'ouvre EN POPUP, et non en quittant l'agenda pour le
      // registre entier : on consulte ce qu'on a sous les yeux, on ne change
      // pas d'écran pour lire une ligne.
      () => setOccurrence(o),
      o.heure, o.responsableLibelle, true, [o.responsableKey], o.posteTenu);
  });

  const first = new Date(ym.y, ym.m, 1);
  const startDow = (first.getDay() + 6) % 7; // lundi = 0
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const today = aujourdhuiIso();
  const monthName = libelleMois(`${ym.y}-${String(ym.m + 1).padStart(2, '0')}`);

  const plusJours = (iso: string, n: number) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  /** Le lundi de la semaine d'un jour — jamais le dimanche : la grille
   *  mensuelle commence lundi, les deux autres vues aussi. */
  const lundiDe = (iso: string) => plusJours(iso, -((new Date(`${iso}T00:00:00`).getDay() + 6) % 7));
  const joursSemaineDe = (iso: string) => Array.from({ length: 7 }, (_, i) => plusJours(lundiDe(iso), i));
  const fmtJour = (iso: string) => libelleJourLong(iso);

  // ‹ / Aujourd'hui / › naviguent dans l'unité de la vue : mois, semaine, jour.
  const nav = (d: number) => {
    if (vue === 'mois') setYm(({ y, m }) => { const dt = new Date(y, m + d, 1); return { y: dt.getFullYear(), m: dt.getMonth() }; });
    else setAncre((a0) => plusJours(a0, vue === 'semaine' ? d * 7 : d));
  };
  const aujourdhuiNav = () => {
    setYm({ y: new Date().getFullYear(), m: new Date().getMonth() });
    setAncre(today);
  };
  const semaine = joursSemaineDe(ancre);
  /* LES CONVENTIONS AVEC LES CLINIQUES, en repère discret (RQ, 04/10/2026) : un
     petit point sous la date, le nom à l'infobulle ; en vue jour, une ligne grise.
     Ce ne sont pas des événements — ils ne comptent ni dans « +N » ni dans le filtre.
     Lues sur la plage affichée ; sans droit sur le planning, rien ne s'affiche. */
  const finMois = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(days).padStart(2, '0')}`;
  const debutMois = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-01`;
  const [convDu, convAu] = vue === 'mois' ? [debutMois, finMois] : vue === 'semaine' ? [semaine[0]!, semaine[6]!] : [ancre, ancre];
  const { occurrences: occConventions } = useOccurrences(convDu, convAu);
  /* Les conventions et événements des contacts sont des ÉVÉNEMENTS depuis le
     12/10/2026, et non plus de petits points : un rendez-vous ponctuel (visite
     de la CNAM) ne se lit pas à l'infobulle. Un clic ouvre le carnet. */
  occConventions.forEach((o) => push('conventions', o.date, `${o.nom}${o.semaine ? ` · ${o.semaine}` : ''}`, 'var(--violet-600)', () => go('annuaire')));
  /* MON PLANNING et LES FÉRIÉS — une seule route, ouverte à tout compte et
     filtrée sur l'appelant (`mon-planning`). Les fériés y sont embarqués : la
     route `feries` est gardée par le planning. */
  const [monPlanning, setMonPlanning] = React.useState<{ du: string; au: string; affectations: { date: string; nature: string; poste: { code: string; libelle: string } | null }[]; feries: { date: string; libelle: string; chome: boolean }[] } | null>(null);
  React.useEffect(() => {
    if (!familles.has('planning') && !familles.has('feries')) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/rh/mon-planning?du=${convDu}&au=${convAu}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant && d) setMonPlanning({ du: convDu, au: convAu, affectations: d.affectations || [], feries: d.feries || [] }); })
      .catch(() => { /* couche absente : les autres restent lisibles */ });
    return () => { vivant = false; };
  }, [convDu, convAu, familles]);
  if (monPlanning?.du === convDu && monPlanning.au === convAu) {
    monPlanning.affectations.forEach((a) => push('planning', a.date, `Mon poste : ${a.poste?.libelle || a.poste?.code || 'poste'}${a.nature && a.nature !== 'travail' ? ` (${a.nature})` : ''}`, 'var(--cyan-700)', () => go('monplanning')));
    /* Le férié s'écrit par le MÊME module que « Mon planning » (`lib/feries`) :
       libellé raccourci (les noms réels font 90 caractères), même couleur. */
    monPlanning.feries.forEach((f) => push('feries', f.date, `Férié — ${libelleCourtFerie(f.libelle)}`, COULEUR_FERIE.texte, () => go('calendrier')));
  }
  /* LES ÉCHÉANCES QUALITÉ — la couche R9 (`GET /ged/cq/calendrier`) : lue,
     jamais recopiée. Un refus de la route (droit) n'est pas un « rien ». */
  const [qualite, setQualite] = React.useState<{ cle: string; evts: { date: string; libelle: string; detail: string; couleur: string; cible: { view: string; cle?: string; valeur?: string } }[] } | null>(null);
  const [qualiteRefusee, setQualiteRefusee] = React.useState(false);
  React.useEffect(() => {
    if (!familles.has('qualite')) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/cq/calendrier?du=${convDu}&au=${convAu}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (vivant) { setQualiteRefusee(false); setQualite({ cle: `${convDu}|${convAu}`, evts: d.evenements || [] }); } })
      .catch(() => { if (vivant) setQualiteRefusee(true); });
    return () => { vivant = false; };
  }, [convDu, convAu, familles]);
  if (qualite?.cle === `${convDu}|${convAu}`) {
    const COUL: Record<string, string> = { orange: 'var(--amber-500)', bleu: 'var(--blue-700)', rouge: 'var(--red-600)', vert: 'var(--green-600)' };
    qualite.evts.forEach((e) => push('qualite', e.date, e.libelle, COUL[e.couleur] || 'var(--blue-700)',
      () => naviguerVers(e.cible.view as never, e.cible.cle ? { cle: e.cible.cle as never, valeur: e.cible.valeur ?? null } : undefined)));
  }
  const titre = vue === 'mois' ? monthName
    : vue === 'semaine' ? `semaine du ${fmtJour(semaine[0])} au ${fmtJour(semaine[6])}`
      : fmtJour(ancre);

  return (
    <section style={{ ...card, overflow: 'hidden' }}>
      {/* COMPACT (RQ, 04/10/2026) : en-tête, cases et lignes resserrés — le
          calendrier se consulte d'un coup d'œil, il ne doit pas occuper l'écran. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '7px 14px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
        <I n="clock" s={14} style={{ color: 'var(--color-primary)' }} />
        <h2 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1, minWidth: 160 }} title="Calendrier du laboratoire">Calendrier — <span style={{ textTransform: vue === 'mois' ? 'capitalize' : 'none' }}>{titre}</span></h2>
        <span role="group" aria-label="Granularité du calendrier" style={{ display: 'inline-flex', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 2 }}>
          {([['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']] as const).map(([k, l]) => (
            <button key={k} onClick={() => setVue(k)} aria-pressed={vue === k} className="ged-btn"
              /* `minHeight: 26` et 12,5 px (relevé du 27/08/2026, P4b/P5b) : la
                 commande segmentée faisait 21 px de haut, sous le seuil de 24.
                 26 + les 2 px de marge du groupe portent l'ensemble à 30. */
              style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, border: 'none', borderRadius: 'var(--radius-xs)', padding: '5px 11px', minHeight: 26, cursor: 'pointer', background: vue === k ? 'var(--surface-card)' : 'transparent', color: vue === k ? 'var(--navy-900)' : 'var(--text-muted)', boxShadow: vue === k ? 'var(--shadow-sm)' : 'none' }}>
              {l}
            </button>
          ))}
        </span>
        {/* ── LE FILTRE, EN BOUT DE BARRE (RQ, 26/08/2026) ────────────────
            Il était coincé ENTRE « Aujourd'hui » et la flèche suivante, coupant
            en deux un groupe de navigation qui se lit d'un bloc : ‹ · Aujourd'hui
            · ›. Il rejoint donc l'extrémité, après la navigation — c'est un
            réglage de la vue, pas un déplacement dans le temps. */}
        <span role="group" aria-label="Navigation" style={{ display: 'inline-flex', gap: 6 }}>
          <Bouton taille="sm" onClick={() => nav(-1)} aria-label={vue === 'mois' ? 'Mois précédent' : vue === 'semaine' ? 'Semaine précédente' : 'Jour précédent'}>‹</Bouton>
          <Bouton taille="sm" onClick={aujourdhuiNav}>Aujourd’hui</Bouton>
          <Bouton taille="sm" onClick={() => nav(1)} aria-label={vue === 'mois' ? 'Mois suivant' : vue === 'semaine' ? 'Semaine suivante' : 'Jour suivant'}>›</Bouton>
        </span>

        {/* ⚠️ LE FILTRE ANNONCE TOUJOURS CE QU'IL CACHE. Un calendrier qui
            masque sans le dire se lit « il n'y a rien » — et l'on cherche
            ailleurs une échéance qui était sous les yeux. Le mot « Afficher… »
            disparaît (l'entonnoir suffit, RQ 26/08), mais PAS le compte des
            familles masquées : ce n'est pas un libellé, c'est un avertissement.
            Il reste donc, sous forme de nombre collé à l'icône. */}
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <Bouton variante={masquees ? 'primaire' : 'contour'} taille="sm" onClick={() => setFiltreOuvert(!filtreOuvert)} aria-expanded={filtreOuvert} aria-haspopup="true" aria-label={masquees ? `Filtrer — ${masquees} famille(s) masquée(s)` : 'Filtrer ce que le calendrier affiche'} title={masquees ? `${masquees} famille(s) masquée(s)` : 'Choisir ce que le calendrier affiche'}>
            <I n="filter" s={13} />
            {!!masquees && <span style={{ fontSize: 12.5, fontWeight: 700, marginInlineStart: 4 }}>{masquees}</span>}
          </Bouton>

          {filtreOuvert && (
            <>
              {/* Le voile qui referme au clic extérieur. Sans lui, une liste
                  déroulante reste ouverte pendant qu'on travaille ailleurs, et
                  recouvre le calendrier qu'elle sert à régler. */}
              <div onClick={() => setFiltreOuvert(false)} aria-hidden
                style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div role="menu" aria-label="Familles affichées" style={{
                position: 'absolute', insetInlineEnd: 0, top: 'calc(100% + 6px)', zIndex: 41,
                minWidth: 232, background: 'var(--surface-card)', border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: 4,
              }}>
                {/* UNE LIGNE PAR FAMILLE, cliquable sur toute sa largeur — et
                    non des pastilles à cocher dans un encadré (RQ, 26/08). La
                    coche dit l'état, la couleur dit laquelle : les deux se
                    retrouvent tels quels sur les événements du calendrier. */}
                {FAMILLES.map((f) => {
                  const vu = familles.has(f.cle);
                  return (
                    <button key={f.cle} type="button" role="menuitemcheckbox" aria-checked={vu}
                      onClick={() => basculerFamille(f.cle)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                        fontFamily: 'var(--font-sans)', fontSize: 12.5, textAlign: 'start',
                        color: vu ? 'var(--text-body)' : 'var(--text-muted)',
                      }}>
                      <I n={vu ? 'check' : 'x'} s={12} style={{ opacity: vu ? 1 : 0.35 }} />
                      <span style={{ width: 9, height: 9, borderRadius: 'var(--radius-xs)', background: f.couleur, flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{f.libelle}{f.cle === 'qualite' && qualiteRefusee ? ' — non accessible' : ''}</span>
                    </button>
                  );
                })}
                <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 4, paddingTop: 4 }}>
                  <button type="button" role="menuitem"
                    onClick={() => { const t = new Set(FAMILLES.map((f) => f.cle)); setFamilles(t); memoriser(t); }}
                    style={{
                      display: 'block', width: '100%', border: 'none', background: 'transparent',
                      cursor: 'pointer', padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                      fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
                      textAlign: 'start', color: 'var(--color-primary-active)',
                    }}>
                    Tout afficher
                  </button>
                  <div style={{ padding: '2px 10px 6px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                    Votre choix est gardé sur cet appareil.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* minmax(0,1fr) et NON 1fr : `1fr` vaut `minmax(auto, 1fr)`, dont le
          minimum `auto` interdit à une colonne de passer sous la largeur
          min-content de son contenu — un libellé d'événement long élargissait
          donc sa colonne. Avec un minimum de 0, les 7 colonnes font exactement
          1/7 quel que soit le contenu, et l'ellipsis des libellés opère. */}
      {vue !== 'jour' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border-subtle)' }}>
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
          <div key={d} style={{ padding: '3px 6px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', textAlign: 'center' }}>{d}</div>
        ))}
      </div>
      )}

      {/* ── Vue SEMAINE : 7 jours à partir du lundi, tous les événements ──── */}
      {vue === 'semaine' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {semaine.map((key) => {
            const evs = [...(events[key] || [])].sort(parHeure);
            const isToday = key === today;
            return (
              <div key={key} style={{ minHeight: 96, borderTop: '1px solid var(--border-subtle)', borderInlineStart: '1px solid var(--border-subtle)', padding: '4px 6px', background: isToday ? 'var(--cyan-50)' : 'var(--surface-card)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {/* Même geste depuis la semaine : le manque était le même. */}
                  <button type="button" onClick={() => { setAncre(key); setVue('jour'); }}
                    title={`Voir la journée du ${key.slice(8)}/${key.slice(5, 7)} en détail`}
                    aria-label={`Voir la journée du ${key.slice(8)}/${key.slice(5, 7)} en détail`}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--color-primary-active)' : 'var(--text-muted)', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                    {key.slice(8)}
                  </button>
                    <span style={{ flex: 1 }} />
                </div>
                {evs.map((e, j) => (
                  <button key={j} onClick={e.ouvrir} className="ged-btn"
                    title={`${e.label}${e.assigne ? ` — ${sansPlanning(e.assigne)}${e.poste ? ` (${e.poste})` : ''}` : orphelin(e) ? ' — PERSONNE n’est assigné' : ''} — ouvrir`}
                    style={{ display: 'block', width: '100%', textAlign: 'start', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 600, color: 'var(--text-on-primary)', background: e.color, border: orphelin(e) ? `1.5px solid ${BORD_ORPHELIN}` : 'none', borderRadius: 'var(--radius-xs)', padding: '1px 5px', marginTop: 2, cursor: 'pointer' }}>
                    {/* La case du mois est étroite : on n'y met pas le nom, mais
                        l'absence d'assigné se voit au liseré et se lit à
                        l'infobulle. Le nom complet est dans les vues jour et
                        semaine, où il y a la place. */}
                    {e.label}
                  </button>
                ))}
                {evs.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 6, textAlign: 'center' }}>—</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Vue JOUR : la liste complète du jour, lisible sans dépliage ───── */}
      {vue === 'jour' && (
        (() => {
          const evsJour = [...(events[ancre] || [])].sort(parHeure);
          /* LA COLONNE DE L'HEURE PREND LA LARGEUR DE SON CONTENU (RQ,
             04/10/2026) : une grille partagée (`subgrid`) dont la première
             colonne vaut `max-content` — la plus longue plage du jour, pas
             92 px fixes. Sans aucune heure ce jour-là, la colonne disparaît. */
          const avecHeure = evsJour.some((e) => !!e.heure);
          return (
        <div style={{ padding: '6px 14px', display: 'grid', gap: 4,
          gridTemplateColumns: avecHeure ? 'max-content minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto' }}>
          {/* La journée se lit DANS SON ORDRE : les événements à plage horaire
              d'abord, dans l'ordre de la plage, puis ceux qui n'en portent pas. */}
          {evsJour.map((e, j) => (
            <button key={j} onClick={e.ouvrir} title={`${e.label}${e.assigne ? ` — ${sansPlanning(e.assigne)}` : ''} — ouvrir`} className="ged-btn"
              style={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1 / -1', alignItems: 'center', columnGap: 10, textAlign: 'start', border: `1px solid ${orphelin(e) ? BORD_ORPHELIN : 'var(--border-subtle)'}`, borderInlineStart: `4px solid ${e.color}`, borderRadius: 'var(--radius-sm)', background: orphelin(e) ? FOND_ORPHELIN : 'var(--surface-card)', padding: '4px 10px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>
              {avecHeure && (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {e.heure || ''}
                </span>
              )}
              <span style={{ minWidth: 0 }}>{e.label}</span>
              {/* À QUI : le rond aux initiales, puis le CODE du poste quand la
                  personne vient du planning. Une tâche sans destinataire se
                  nomme — c'est un oubli d'organisation. */}
              {quiDe(e).length ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                  {quiDe(e).map((q) => <Rond key={q.cle} {...q} />)}
                  {e.poste && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)' }}>{e.poste}</span>}
                </span>
              ) : orphelin(e) ? (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: BORD_ORPHELIN, whiteSpace: 'nowrap' }}>
                  personne n’est assigné
                </span>
              ) : <span />}
            </button>
          ))}
          {(events[ancre] || []).length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '4px 0', textAlign: 'center' }}>
              Rien au calendrier ce jour-là.
            </div>
          )}
        </div>
          );
        })()
      )}

      {/* ── Vue MOIS (grille historique) ──────────────────────────────────── */}
      {vue === 'mois' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {Array.from({ length: startDow }).map((_, i) => <div key={`e${i}`} style={{ minHeight: 44, borderTop: '1px solid var(--border-subtle)', background: 'var(--gray-25)' }} />)}
        {Array.from({ length: days }).map((_, i) => {
          const key = `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
          const evs = [...(events[key] || [])].sort(parHeure);
          const isToday = key === today;
          return (
            <div key={key} style={{ position: 'relative', minHeight: plein ? 96 : 44, borderTop: '1px solid var(--border-subtle)', borderInlineStart: '1px solid var(--border-subtle)', padding: '4px 6px', background: isToday ? 'var(--cyan-50)' : 'var(--surface-card)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {/* ── LE NUMÉRO DU JOUR OUVRE LE MODE JOUR (RQ, 05/10/2026) ──
                    « Quand je clique sur une journée, je veux la voir en mode
                    jour. » Le mode jour EXISTE déjà sur cet agenda — on n'en
                    construit pas un second, on le rend atteignable.
                    ⚠️ C'est le NUMÉRO qui est le bouton, pas la case : la case
                    contient déjà les boutons d'événement, et rendre le fond
                    cliquable aurait fait d'un clic sur un événement un clic
                    ambigu — ou volé le clic à l'événement. */}
                <button type="button" onClick={() => { setAncre(key); setVue('jour'); }}
                  title={`Voir la journée du ${key.slice(8)}/${key.slice(5, 7)} en détail`}
                  aria-label={`Voir la journée du ${key.slice(8)}/${key.slice(5, 7)} en détail`}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--color-primary-active)' : 'var(--text-muted)', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                  {i + 1}
                </button>
                <span style={{ flex: 1 }} />
              </div>
              {(jourOuvert === key ? evs : evs.slice(0, parCase)).map((e, j) => (
                <button key={j} onClick={e.ouvrir} className="ged-btn"
                  title={`${e.label}${e.assigne ? ` — ${sansPlanning(e.assigne)}${e.poste ? ` (${e.poste})` : ''}` : orphelin(e) ? ' — PERSONNE n’est assigné' : ''} — ouvrir`}
                  style={{ display: 'block', width: '100%', textAlign: 'start', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 600, color: 'var(--text-on-primary)', background: e.color, border: orphelin(e) ? `1.5px solid ${BORD_ORPHELIN}` : 'none', borderRadius: 'var(--radius-xs)', padding: '1px 5px', marginTop: 2, cursor: 'pointer' }}>
                  {e.label}
                  {/* La colonne d'une semaine tient un nom court, pas une
                      phrase : on met l'assigné derrière un point médian, et
                      l'absence se dit en clair — c'est elle qui doit sauter aux
                      yeux, pas la présence. */}
                  {quiDe(e).length
                    ? <span style={{ opacity: .85 }}> · {quiDe(e).map((q) => q.initiales).join(' ')}{e.poste ? ` ${e.poste}` : ''}</span>
                    : orphelin(e) ? <span style={{ opacity: .95 }}> · personne</span> : null}
                </button>
              ))}
              {evs.length > parCase && (
                <button onClick={() => setJourOuvert(jourOuvert === key ? null : key)} className="ged-btn"
                  title={jourOuvert === key ? 'Replier' : `Afficher les ${evs.length - parCase} autres événements du jour`}
                  style={{ background: 'none', border: 'none', padding: 0, marginTop: 1, cursor: 'pointer', font: 'inherit', fontSize: 9.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>
                  {jourOuvert === key ? '− replier' : `+${evs.length - parCase}`}
                </button>
              )}
            </div>
          );
        })}
      </div>
      )}
      {/* ── Une occurrence de maintenance, ouverte depuis l'agenda ──────────
          Elle ne s'ouvre PAS en panneau de fiche : une occurrence n'est pas un
          enregistrement, c'est une PROJECTION — le moteur la déduit de la
          fréquence, du calendrier d'ouverture et de la politique de report. Il
          n'y a donc pas de « fiche » à afficher, et faire semblant en donnerait
          une qu'on croirait modifiable. On montre ce que la projection dit, on
          nomme d'où viennent la date et le destinataire, et on offre les deux
          vraies destinations : l'appareil, et le registre. */}
      {/* ── MODIFIER LA MAINTENANCE, DEPUIS L'AGENDA ─────────────────────
          Demande de la RQ le 14/08/2026 : « quand je clique sur modifier une
          maintenance à partir de l'agenda du jour, j'atterris dans toutes les
          maintenances préventives ; je veux atterrir en pop-up sur la
          maintenance d'intérêt et la modifier directement ».

          L'occurrence n'est pas modifiable — c'est une projection. Mais la
          LIGNE DE PLAN dont elle découle l'est, et l'occurrence en porte
          l'identifiant (`planId`). On va donc chercher cette ligne et on ouvre
          le formulaire du registre, ici, sans quitter l'agenda ni chercher la
          bonne ligne dans une liste de quatre-vingt-cinq. */}
      {aAcquitter && (
        <Acquitter occ={aAcquitter}
          ligne={(registreMaintenance?.lignes || []).find((l) => l.documentId === aAcquitter.planId)}
          onFerme={() => setAAcquitter(null)}
          onFait={() => { setAAcquitter(null); setOccurrence(null); void rechargerAgenda(); }} />
      )}
      {ligneAModifier && registreMaintenance && (
        <FormulaireLigne
          reg={registreMaintenance}
          initial={brouillonDe(ligneAModifier)}
          editId={ligneAModifier.documentId}
          onFerme={() => setLigneAModifier(null)}
          onFait={() => { void rechargerAgenda(); }} />
      )}
      {occurrence && !ligneAModifier && (
        <Modale
          titre={`${occurrence.equipementNom} — ${occurrence.libelle}`}
          sousTitre={`${occurrence.frequenceLibelle || 'fréquence non précisée'} · ${fmtDate(occurrence.date)}${occurrence.heure ? ` · ${occurrence.heure}` : ''}`}
          onClose={() => setOccurrence(null)}
          largeur={620}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* L'ACQUITTEMENT d'abord (RQ, 19/08) : c'est le geste pour
                  lequel on clique une échéance du jour. Mis en avant quand la
                  maintenance me revient — posée sur moi au plan, ou dérivée de
                  mon affectation du jour — mais ouvert à tout rôle GED, comme
                  au registre : remplacer au pied levé ne doit pas exiger de
                  changer le plan. */}
              <Bouton variante={occurrence.responsableKey === session.key ? 'primaire' : 'contour'} taille="sm" onClick={() => setAAcquitter(occurrence)} title={occurrence.responsableKey === session.key
                  ? 'Cette maintenance vous revient — déclarez-la faite ici, sans passer par le registre.'
                  : `Revient à ${occurrence.responsableLibelle || 'quelqu’un d’autre'} — vous pouvez néanmoins la déclarer faite.`}>
                <I n="check" s={13} />Déclarer faite
              </Bouton>
              <Bouton taille="sm" onClick={() => { const c = occurrence.equipementCode; setOccurrence(null); ouvrirPanneau({ type: 'equip', valeur: c, origine: 'Agenda' }); }}>Ouvrir l’appareil</Bouton>
              {peutTenirLePlan && (
                <Bouton variante="primaire" taille="sm" onClick={() => void ouvrirModification(occurrence.planId)}>Modifier cette maintenance</Bouton>
              )}
              <Bouton taille="sm" onClick={() => { setOccurrence(null); go('maintenances'); }}>Aller au registre</Bouton>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setOccurrence(null)}>Fermer</Bouton>
            </div>
          )}>
          <dl className="ged-2col" style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
            {[
              ['Appareil', `${occurrence.equipementNom} (${occurrence.equipementCode})`],
              ['Maintenance', occurrence.libelle],
              ['Fréquence', occurrence.frequenceLibelle || occurrence.frequence || '—'],
              ['Plage horaire', occurrence.heure || 'non précisée'],
              ['Paillasse', occurrence.paillasse || '—'],
              ['Date à tenir', fmtDate(occurrence.date)],
              /* ⚠️ LE POSTE À OCCUPER EST RENDU MÊME VIDE (RQ, 26/08/2026).
                 « Aucun destinataire » ne dit pas quoi faire ; « poste
                 P2_CHIMIE, personne n’y est affecté aujourd’hui » le dit. */
              ['Poste à occuper', (occurrence.postesAOccuper || []).length
                ? (occurrence.postesAOccuper || []).join(' · ')
                : 'aucun poste rattaché à cet appareil'],
              ['Destinataire', occurrence.responsableLibelle || 'personne n’est affecté à ce poste aujourd’hui'],
            ].map(([k, v]) => (
              <div key={String(k)}>
                <dt style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{k}</dt>
                <dd style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.45 }}>{v}</dd>
              </div>
            ))}
          </dl>
          {/* Quand l'appareil n'est rattaché à AUCUN poste, aucun planning n'y
              remédiera : le dire évite d'envoyer remplir un planning pour rien.
              C'est le pendant du bandeau ambre du registre des maintenances. */}
          {!(occurrence.postesAOccuper || []).length && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.5, fontWeight: 500 }}>
              Cet appareil n’est rattaché à aucun poste de travail : personne ne pourra jamais être
              désigné, quel que soit le planning. Rattachez-le depuis Postes → Équipements.
            </p>
          )}
          {(occurrence.postesAOccuper || []).length > 0 && !occurrence.responsableLibelle && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>
              Le poste existe, mais personne n’y est affecté le {fmtDate(occurrence.date)} —
              complétez le planning pour que cette maintenance trouve son destinataire.
            </p>
          )}
          {occurrence.responsableSource && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {occurrence.responsableSource === 'plan'
                ? 'Destinataire désigné sur la ligne de plan : il ne change pas avec le planning.'
                : 'Destinataire déduit de l’affectation du jour sur le poste de l’appareil : il changera si le planning change.'}
            </p>
          )}
          {occurrence.deplacee && (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>
              Échéance déplacée — la fréquence la posait au {fmtDate(occurrence.dateTheorique)}.
              {occurrence.motifReport ? ` ${occurrence.motifReport}` : ''}
            </p>
          )}
          <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.5 }}>
            Cette ligne est <strong>projetée</strong> à partir de la fréquence, du calendrier d’ouverture
            du laboratoire et de la politique de report : elle n’est pas un enregistrement et ne se
            modifie pas ici. La consigner comme faite se fait au registre des maintenances.
          </p>
        </Modale>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * DEMANDES À TRAITER (RQ, 04/10/2026) — ce qui ATTEND une décision, réuni en
 * un seul encadré. Chaque carte compte, montre les plus anciennes, et mène à
 * SON écran — on ne traite rien ici, on voit ce qui attend.
 *
 * ── QUI VOIT QUOI : la carte suit le droit de TRAITER, pas celui de lire ───
 * Une demande qu'on ne peut pas décider n'est pas « à traiter » pour soi : la
 * montrer ferait une liste de reproches sans prise. D'où :
 *   · rendez-vous             → qui MODIFIE la rubrique « Demandes de RDV »
 *                               ET tient aujourd'hui un poste d'accueil ou de
 *                               prélèvement (`POSTES_ACCUEIL`) ;
 *   · suggestions, retours    → qui peut leur donner suite (le serveur le dit :
 *                               `peutTraiter`, aujourd'hui le RQ) ;
 *   · NC non prises en charge,
 *     demandes de documents   → le RQ (il prend en charge, il statue) ;
 *   · inscriptions            → le super admin (seul à créer un compte) ;
 *   · congés, échanges        → le RH (seul à décider, la paie en dépend).
 * L'encadré entier disparaît pour qui n'a aucune de ces cartes.
 *
 * ⚠️ Une source illisible (droit refusé, serveur muet) fait DISPARAÎTRE sa
 * carte au lieu d'afficher « 0 » : un zéro est une affirmation, et on ne la
 * fait pas sur ce qu'on n'a pas pu lire.
 * ═════════════════════════════════════════════════════════════════════════ */
type ElementAttente = { cle: string; ref: string; texte: string; date: string; onClick?: () => void };
type Source = { code: string; titre: string; ouvrir: () => void; elements: ElementAttente[] | null; aide: string };
type Lu = ElementAttente[] | null | 'refus';

/** Le nom tel que le SERVEUR l'a déjà composé (`redigerAbsence` → `composerNom`) : on ne le recompose pas ici. */
const nomServi = (p: { nom: string } | null) => (p ? p['nom'] : '') || '—';

/* ⚠️ LES RENDEZ-VOUS SE TRAITENT À L'ACCUEIL (Inès, 02/11/2026). Le droit de
   modifier la rubrique ne suffit plus : la carte ne s'affiche qu'à qui TIENT
   aujourd'hui l'un de ces postes au planning — les trois accueils et les deux
   salles de prélèvement du matin, l'accueil et le prélèvement de l'après-midi,
   et la réception du dimanche.
   Un poste lu par CODE, comme partout où le planning est cité : renommer un
   poste ne retire pas la carte, en ajouter un à l'accueil demande de l'écrire ici. */
const POSTES_ACCUEIL = ['ACCUEIL1', 'ACCUEIL2', 'ACCUEIL3', 'ACCUEILPM', 'PLV1', 'PLV2', 'PLVPM', 'DIMRECEP'];

function DemandesATraiter() {
  const { demandes, ncs, settings, session, rhAcces, go, openSettings, ouvrirPanneau, loading, booted, docs } = useGed();
  const isRQ = session.roles.includes('rq');
  const estRh = !!rhAcces?.acces;
  const estSuperAdmin = session.profile === 'superadmin';
  /* Le poste du jour vient du mémo (`mon-jour`) : la même lecture qui dit à
     chacun ce qu'il tient. Un congé validé ou une ligne d'absence n'est pas
     « être à l'accueil ». Tant qu'elle n'a pas répondu, pas de carte. */
  const [aLAccueil, setALAccueil] = React.useState(false);
  React.useEffect(() => {
    let vivant = true;
    fetch('/api/ged/ged/rh/mon-jour', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { ok?: boolean; enConge?: boolean; postes?: { code: string; nature: string }[] } | null) => {
        if (vivant) setALAccueil(!!d?.ok && !d.enConge
          && (d.postes || []).some((p) => p.nature !== 'absence' && POSTES_ACCUEIL.includes(p.code)));
      })
      .catch(() => { if (vivant) setALAccueil(false); });
    return () => { vivant = false; };
  }, [session.key]);
  const voitRdv = aLAccueil && droitsNav(session, estRh).section('rdv', 'modifier');

  const [rdv, setRdv] = React.useState<Lu>(null);
  const [sugg, setSugg] = React.useState<Lu>(null);
  const [retours, setRetours] = React.useState<Lu>(null);
  const [inscriptions, setInscriptions] = React.useState<Lu>(null);
  const [conges, setConges] = React.useState<Lu>(null);
  const [echanges, setEchanges] = React.useState<Lu>(null);

  React.useEffect(() => {
    let vivant = true;
    const lire = async <T,>(url: string, f: (j: T) => ElementAttente[] | 'refus', set: (v: Lu) => void) => {
      try {
        const r = await fetch(url, { credentials: 'include' });
        if (!vivant) return;
        if (!r.ok) { set('refus'); return; }
        set(f(await r.json() as T));
      } catch { if (vivant) set('refus'); }
    };
    const anciennete = (a: ElementAttente, b: ElementAttente) => String(a.date).localeCompare(String(b.date));
    // Rendez-vous NOUVEAUX (ni confirmés ni traités) — sans le nom du patient ici.
    if (voitRdv) void lire<{ data?: { documentId: string; ref: string; preferredDate: string; createdAt: string }[] }>(
      '/api/ged/appointments?filters[rdvStatus][$eq]=new&fields[0]=ref&fields[1]=preferredDate&fields[2]=createdAt&sort=createdAt:asc&pagination[pageSize]=100',
      (j) => (j.data || []).map((x) => ({ cle: x.documentId, ref: x.ref, texte: x.preferredDate ? `souhaité le ${fmtDate(x.preferredDate)}` : 'date libre', date: x.createdAt })),
      setRdv);
    void lire<{ peutTraiter?: boolean; lignes?: { documentId: string; ref: string; titre: string; statut: string; dateDepot: string }[] }>(
      '/api/ged/ged/suggestions',
      (j) => (!j.peutTraiter ? 'refus' : (j.lignes || []).filter((l) => ['deposee', 'lue'].includes(l.statut))
        .map((l) => ({ cle: l.documentId, ref: l.ref, texte: l.titre, date: l.dateDepot })).sort(anciennete)),
      setSugg);
    void lire<{ peutTraiter?: boolean; lignes?: { documentId: string; ref: string; message: string; statut: string; dateDepot: string }[] }>(
      '/api/ged/ged/retours',
      (j) => (!j.peutTraiter ? 'refus' : (j.lignes || []).filter((l) => ['recu', 'lu'].includes(l.statut))
        .map((l) => ({ cle: l.documentId, ref: l.ref, texte: String(l.message || '').slice(0, 90), date: l.dateDepot })).sort(anciennete)),
      setRetours);
    if (estSuperAdmin) void lire<{ id: string; ref?: string; prenom: string; nom: string; institution: string; status: string; createdAt: string | null }[]>(
      '/api/team/requests',
      (j) => (Array.isArray(j) ? j : []).filter((x) => x.status === 'pending')
        .map((x) => ({ cle: x.id, ref: x.ref || 'Compte', texte: `${x.prenom} ${x.nom}${x.institution ? ` — ${x.institution}` : ''}`, date: x.createdAt || '' })).sort(anciennete),
      setInscriptions);
    if (estRh) {
      void lire<{ absences?: { documentId: string; statut: string; dateDebut: string; dateFin: string; motifLabel?: string; dateDemande?: string; personnel: { nom: string } | null }[] } | { documentId: string }[]>(
        '/api/ged/ged/rh/absences',
        (j) => {
          const l = (Array.isArray(j) ? j : (j.absences || [])) as { documentId: string; statut: string; dateDebut: string; dateFin: string; motifLabel?: string; dateDemande?: string; personnel: { nom: string } | null }[];
          return l.filter((a) => a.statut === 'demandee')
            .map((a) => ({ cle: a.documentId, ref: nomServi(a.personnel), texte: `${a.motifLabel || 'Absence'} · ${fmtDate(a.dateDebut)}${a.dateFin && a.dateFin !== a.dateDebut ? ` → ${fmtDate(a.dateFin)}` : ''}`, date: a.dateDemande || a.dateDebut })).sort(anciennete);
        },
        setConges);
      void lire<{ echanges?: { id: string; type: string; statut: string; le: string; demandeurNom: string; remplacantNom: string; cedees: { date: string; posteLibelle: string }[] }[] }>(
        '/api/ged/ged/rh/echanges',
        (j) => (j.echanges || []).filter((e) => e.statut === 'demandee')
          .map((e) => ({ cle: e.id, ref: e.type === 'cession' ? 'Cession' : 'Échange', texte: `${e.demandeurNom} → ${e.remplacantNom} · ${e.cedees.map((x) => `${fmtDate(x.date)} ${x.posteLibelle}`).join(', ')}`, date: e.le })).sort(anciennete),
        setEchanges);
    }
    return () => { vivant = false; };
  }, [voitRdv, estSuperAdmin, estRh]);

  const pret = booted && !(loading && docs.length === 0);
  /* ⚠️ DEUX CERCLES DANS UNE SEULE CARTE (07/10/2026). Une déclaration
     « soumise » se tranche par le RQ ET le CQ (`peutTrancherNC`) ; une NC
     « déclarée » se prend en charge à la lecture, par le RQ et son suppléant.
     Le coordinateur qualité qui n'est pas RQ ne voit donc que les premières :
     lui lister des NC qu'il ne porte pas lui ferait une liste à surveiller
     sans rien à y faire. */
  const peutTrancher = peutTrancherNC(session.roles, session.actes);
  const statutsAttendus = isRQ ? ['soumise', 'declaree'] : ['soumise'];
  const ncNonPrises = ncs.filter((n) => statutsAttendus.includes(String(n.statut)))
    .sort((a, b) => String(a.dateDeclaration).localeCompare(String(b.dateDeclaration)))
    .map((n) => ({ cle: n.documentId, ref: n.ref, texte: n.titre, date: n.dateDeclaration,
      onClick: () => ouvrirPanneau({ type: 'nc', valeur: n.documentId, origine: 'Tableau de bord' }) }));
  const demDocs = demandes.filter((d) => !d.decision || d.decision === 'discuter')
    .map((r) => ({ cle: r.documentId, ref: r.propositions?.code || '—',
      texte: `${r.propositions?.titre || ''} — ${userName(settings, r.demandeur)}${r.decision === 'discuter' ? ' · à discuter' : ''}`, date: r.createdAt || '' }));

  const carte = (v: Lu, s: Omit<Source, 'elements'>): Source | null => (v === 'refus' ? null : { ...s, elements: v });
  const sources = [
    !voitRdv ? null : carte(rdv, { code: 'rdv', titre: 'Rendez-vous', ouvrir: () => go('rdv'), aide: 'Nouvelles demandes du site, ni confirmées ni traitées' }),
    (isRQ || peutTrancher)
      ? { code: 'nc',
        titre: isRQ ? 'NC non prises en charge' : 'Déclarations à trancher',
        ouvrir: () => go('nc'),
        elements: pret ? ncNonPrises : null,
        aide: isRQ ? 'Déclarées, pas encore ouvertes par le RQ' : 'Soumises : à retenir au registre, ou à rejeter d’office' }
      : null,
    isRQ ? { code: 'req', titre: 'Demandes de documents', ouvrir: () => go('req'), elements: pret ? demDocs : null, aide: 'Création ou modification de document, en attente de décision' } : null,
    carte(sugg, { code: 'sugg', titre: 'Suggestions du personnel', ouvrir: () => go('suggestions'), aide: 'Déposées ou lues, sans suite donnée' }),
    carte(retours, { code: 'retours', titre: 'Retours des usagers', ouvrir: () => go('retours'), aide: 'Reçus ou lus, sans suite donnée' }),
    !estSuperAdmin ? null : carte(inscriptions, { code: 'inscr', titre: 'Inscriptions', ouvrir: () => openSettings('utilisateurs'), aide: 'Demandes de compte déposées depuis la page de connexion' }),
    !estRh ? null : carte(conges, { code: 'conges', titre: 'Congés & absences', ouvrir: () => go('absences'), aide: 'Demandes en attente de décision RH' }),
    !estRh ? null : carte(echanges, { code: 'echanges', titre: 'Échanges de planning', ouvrir: () => go('planning'), aide: 'Échanges et cessions en attente de validation RH' }),
  ].filter((x): x is Source => !!x);

  if (!sources.length) return null;

  return (
    <section style={{ ...card, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
        <I n="req" s={15} style={{ color: 'var(--color-primary)' }} />
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Demandes à traiter</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
        {sources.map((src) => (
          <div key={src.code} style={{ borderTop: '1px solid var(--border-subtle)', borderInlineEnd: '1px solid var(--border-subtle)', padding: '12px 16px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6, alignContent: 'start', minWidth: 0 }}>
            <button onClick={src.ouvrir} title={src.aide} className="ged-btn"
              style={{ display: 'flex', alignItems: 'baseline', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'start', font: 'inherit' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600, lineHeight: 1,
                color: src.elements?.length ? 'var(--navy-900)' : 'var(--text-faint)' }}>
                {src.elements === null ? '…' : src.elements.length}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{src.titre}</span>
            </button>
            {src.elements === null ? <Skeleton rows={1} height={24} />
              : !src.elements.length ? <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Rien en attente.</span>
                : (
                  <>
                    {src.elements.slice(0, 3).map((e) => (
                      <button key={e.cle} onClick={e.onClick || src.ouvrir} className="ged-row"
                        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1, width: '100%', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', textAlign: 'start', font: 'inherit', minWidth: 0 }}>
                        {/* La cellule ne s'élargit jamais à son contenu (minmax(0, 1fr)) : le code et la
                            date débordaient sur la colonne voisine. Faute de place, la date passe à la
                            ligne plutôt que de tronquer le code, qui est ce qu'on cherche. */}
                        <span style={{ display: 'flex', flexWrap: 'wrap', columnGap: 8, alignItems: 'baseline' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--color-primary-active)', whiteSpace: 'nowrap' }}>{e.ref}</span>
                          <span style={{ marginInlineStart: 'auto', fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</span>
                        </span>
                        {/* Sur sa propre ligne : à côté du code et de la date, il ne restait que trois lettres. */}
                        <span style={{ fontSize: 12.5, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.texte}</span>
                      </button>
                    ))}
                    {src.elements.length > 3 && (
                      <Bouton taille="sm" onClick={src.ouvrir} style={{ justifySelf: 'start' }}>
                        Voir les {src.elements.length} →
                      </Bouton>
                    )}
                  </>
                )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LES DERNIERS SIGNALEMENTS — POUR APPRENDRE (RQ, 04 puis 05/10/2026).
 *
 * Remplace, pour tout le monde, le constat « N NC sans action au plan » : ce
 * constat parle au gestionnaire du plan d'actions — aujourd'hui le RQ et les
 * responsables — et à eux seuls ; il reste affiché pour eux, en une ligne.
 *
 * Pour la personne qui se connecte, la question utile est : « qu'est-ce qui
 * s'est passé récemment, et qu'est-ce que ça change pour moi ? ». Chaque
 * signalement se lit donc en étapes simples, et seulement les étapes déjà
 * renseignées : une rubrique vide ne s'affiche pas, elle ne s'invente pas.
 *
 * ── LES RÉCLAMATIONS DANS LE MÊME FLUX (RQ, 05/10/2026) ───────────────────
 *
 * « Nous avons opté pour que les NC soient une colonne dans le dashboard. Y
 * rajouter les réclamations dans le même flux. »
 *
 * UNE colonne, DEUX registres, triés ensemble par date (`fondre`). Une
 * réclamation de patient et une NC interne enseignent la même chose à qui
 * arrive le matin ; deux encarts côte à côte auraient surtout obligé à lire
 * deux fois pour savoir ce qui est le plus récent. L'étiquette dit d'où ça
 * vient, la place ne dit que l'ancienneté.
 *
 * ⚠️ LES DEUX MOITIÉS SONT GARDÉES SÉPARÉMENT, et ce n'est pas un détail de
 * droits : une réclamation NOMME un plaignant. La moitié « réclamations » du
 * flux n'est ni affichée ni même demandée au serveur sans la section `recl` —
 * fermée au profil de base. Un technicien voit donc exactement ce qu'il voyait
 * hier : les NC. Le serveur le tient de son côté (`/ged/recl/vues` est
 * rattachée à la section `recl`) : fusionner deux registres à l'écran ne
 * fusionne pas leurs droits.
 *
 * ⚠️ LES RUBRIQUES NE SONT PAS LES MÊMES DE PART ET D'AUTRE, et on ne les a
 * pas forcées à se ressembler : une NC raconte « ce qui s'est passé → corrigé
 * tout de suite → pourquoi → ce qui change » ; une réclamation raconte « ce
 * qui nous est reproché → ce qu'on a répondu ». Plaquer les quatre étapes de
 * la NC sur une réclamation aurait affiché des rubriques vides en permanence,
 * donc l'air d'un dossier mal tenu.
 * ═════════════════════════════════════════════════════════════════════════ */
/* ── LE GLISSEMENT DE « J'AI LU » — durées et feuille locale ──────────────
 *
 * Deux durées, et elles sont dans le code ET dans la feuille : une transition
 * CSS plus longue que le minuteur JavaScript ferait démonter la carte en pleine
 * course, et l'on verrait un saut au lieu d'une sortie. Elles se lisent côte à
 * côte pour qu'un changement n'en oublie pas l'autre.
 *
 * `prefers-reduced-motion` coupe les deux animations : le retrait reste
 * instantané pour qui a demandé moins de mouvement, ce qui est exactement
 * l'ancien comportement — on ne lui doit pas une animation, on lui doit de ne
 * pas en subir.
 */
/** Combien de cartes la fenêtre montre. Nommé parce que l'animation d'entrée
 *  en dépend : la fenêtre PLEINE est le seul cas où retirer une carte en
 *  promeut une autre — sinon les suivantes remontent d'un cran et personne
 *  n'entre, et faire clignoter la dernière annoncerait une arrivée qui n'a pas
 *  eu lieu. */
const NB_CARTES = 4;
/** Ce que l'étiquette de provenance écrit sur la carte. « NC » est le mot du
 *  laboratoire — l'écran, la déclaration et la norme l'emploient tous ;
 *  l'écrire en long ici aurait désaligné les deux étiquettes pour rien. */
const LIBELLE_NATURE: Record<NatureSignalement, string> = { nc: 'NC', recl: 'RÉCLAMATION' };
const DUREE_SORTIE = 220;
const DUREE_ENTREE = 260;
const CSS_GLISSE_NC = `
.db-nc{transition:transform ${DUREE_SORTIE}ms ease, opacity ${DUREE_SORTIE}ms ease;}
.db-nc-sort{transform:translateX(106%);opacity:0;}
.db-nc-entre{animation:db-nc-entre ${DUREE_ENTREE}ms ease both;}
@keyframes db-nc-entre{from{transform:translateY(12px);opacity:0;}to{transform:none;opacity:1;}}
@media (prefers-reduced-motion: reduce){
  .db-nc,.db-nc-entre{transition:none;animation:none;}
  .db-nc-sort{transform:none;}
}
`;

function DerniersSignalements() {
  const { ncs, recls, session, rhAcces, ouvrirPanneau, go, loading, booted, docs } = useGed();
  const droits = droitsNav(session, !!rhAcces?.acces);
  const voitNc = droits.section('nc');
  /* ⚠️ DEUX DROITS, LUS SÉPARÉMENT. Une réclamation nomme un plaignant : la
     section `recl` est fermée au profil de base, et le flux doit s'en tenir à
     ce qui est ouvert à CETTE personne. Déduire l'un de l'autre aurait fait
     d'une colonne fusionnée une fusion des droits. */
  const voitRecl = droits.section('recl');
  const pret = booted && !(loading && docs.length === 0);

  /* ── LES SIGNALEMENTS QUITTÉS, PROPRES À L'APPELANT (RQ, 05/10/2026) ─────
   * ⚠️ ÉTAT LOCAL À CE BLOC, PAS DANS LE MAGASIN, ET C'EST UNE PRÉCAUTION :
   * « Mes NC à traiter » (Mes tâches, plus haut) est une OBLIGATION. Si ce
   * filtre vivait dans le magasin, il finirait par s'y appliquer aussi — et une
   * tâche qu'on se cache reste due. Seul ce bloc-ci, qui est de la pédagogie,
   * obéit au filtre.
   * ⚠️ `null` tant que la liste n'est pas lue : on n'affiche RIEN plutôt que
   * de montrer un signalement qu'on a déjà quitté, puis de le faire disparaître
   * sous les yeux au premier rendu.
   * ⚠️ UN SEUL ENSEMBLE POUR LES DEUX REGISTRES, et c'est licite : un
   * `documentId` Strapi est unique au-delà de sa collection (voir l'en-tête de
   * `signalements-lus.ts`). Deux ensembles à tenir en parallèle auraient surtout
   * fait deux endroits où oublier d'écrire. */
  const [quittees, setQuittees] = React.useState<Set<string> | null>(null);
  const [voirQuittees, setVoirQuittees] = React.useState(false);
  const [enCours, setEnCours] = React.useState<string | null>(null);
  /* ── « J'AI LU » FAIT GLISSER LA CARTE (RQ, 05/10/2026) ───────────────────
   *
   * « J'aimerais qu'elle soit swipée et remplacée par la suivante. »
   *
   * Le retrait était INSTANTANÉ : la ligne disparaissait, les suivantes
   * remontaient d'un cran, et rien ne disait laquelle venait de partir ni
   * laquelle arrivait. Sur une pile de quatre cartes, cela se lit comme un
   * saut — on relit les quatre pour retrouver où l'on en était.
   *
   * La carte sort donc par la droite, puis la liste se recompose et la
   * nouvelle venue — toujours la DERNIÈRE de la fenêtre, puisqu'on en a retiré
   * une au-dessus dans une liste triée — entre à son tour.
   *
   * ⚠️ UNE SEULE À LA FOIS : deux cartes qui glissent ensemble ne se lisent
   * plus, et le second clic viserait une ligne déjà en mouvement.
   * ⚠️ L'ÉCRITURE PART APRÈS LA SORTIE, pas avant : lancée avant, la mise à
   * jour optimiste démonterait la carte au premier rendu et il n'y aurait
   * aucune animation à voir. */
  const [glissante, setGlissante] = React.useState<string | null>(null);
  const [nouvellePromue, setNouvellePromue] = React.useState(false);
  const minuteurs = React.useRef<number[]>([]);
  React.useEffect(() => () => { minuteurs.current.forEach((t) => window.clearTimeout(t)); }, []);

  const relireQuittees = React.useCallback(() => {
    /* Rien à lire pour qui ne voit pas les NC — et SURTOUT pas de `setState`
       ici : un état posé depuis un effet pour une valeur qu'on peut déduire est
       refusé par le lint, à juste titre. Le bloc rend `null` de toute façon
       quelques lignes plus bas, `quittees` n'y est jamais lu. */
    if (!voitNc) return undefined;
    let vivant = true;
    /* ⚠️ LA MOITIÉ « RÉCLAMATIONS » N'EST PAS MÊME DEMANDÉE SANS LE DROIT :
       `/ged/recl/vues` est rattachée à la section `recl` et répondrait 403.
       Appeler pour rien aurait rempli le journal du serveur de refus, et
       surtout laissé croire, en lisant ce code, que le flux fusionné fusionne
       les droits — ce qu'il ne fait pas. */
    type Vues = { vues?: { cible?: string; nc?: string }[] };
    const lire = (url: string): Promise<Vues> => fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { vues: [] }))
      .catch(() => ({ vues: [] }));
    void Promise.all([
      lire('/api/ged/ged/nc/vues'),
      voitRecl ? lire('/api/ged/ged/recl/vues') : Promise.resolve<Vues>({ vues: [] }),
    ]).then((reps) => {
      if (!vivant) return;
      /* `cible` est la clé que rendent les deux routes ; `nc` reste lu en
         second pour qu'un moteur pas encore redéployé ne vide pas la liste
         des lectures — un bloc qui réaffiche tout ce qu'on avait lu se lit
         comme une panne. */
      setQuittees(new Set(reps.flatMap((d) => (d.vues || []).map((v) => String(v.cible || v.nc || ''))).filter(Boolean)));
    });
    return () => { vivant = false; };
  }, [voitNc, voitRecl]);
  React.useEffect(() => relireQuittees(), [relireQuittees]);

  /* ⚠️ LE RETOUR ANTICIPÉ EST **APRÈS** LES CROCHETS, et il l'est devenu le
     05/10/2026 : `if (!voitNc) return null` ouvrait la fonction, si bien que
     les crochets ajoutés en dessous n'étaient appelés que pour une partie des
     visiteurs — l'ordre des crochets changeait d'un rendu à l'autre, et React
     mélange alors les états. Le lanceur le refuse (`audit-crochets-react`), et
     il a eu raison : c'est un défaut, pas une formalité.
     La REQUÊTE, elle, reste gardée — elle ne part pas pour qui ne voit pas les
     NC (`voitNc` dans l'effet), donc aucun appel inutile ni refusé. */
  if (!voitNc) return null;

  /* L'écriture est OPTIMISTE sur l'ensemble local, puis confirmée. En cas
     d'échec on remet la NC : un masquage qui n'a pas pris côté serveur
     reviendrait au prochain chargement, et la liste mentirait entre-temps. */
  /* ⚠️ LA NATURE COMMANDE LA ROUTE. Les deux registres ont leur paire de
     routes, chacune gardée par SA section — il n'existe pas de
     `/ged/:nature/:id/quitter`, et c'est délibéré côté serveur (le
     rattachement route → section y est statique). */
  const basculerQuittee = async (nature: NatureSignalement, documentId: string, quitter: boolean) => {
    setEnCours(documentId);
    setQuittees((s) => {
      const n = new Set(s || []);
      if (quitter) n.add(documentId); else n.delete(documentId);
      return n;
    });
    try {
      const r = await fetch(`/api/ged/ged/${nature}/${encodeURIComponent(documentId)}/quitter`, {
        method: quitter ? 'POST' : 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error('refus');
    } catch {
      setQuittees((s) => {
        const n = new Set(s || []);
        if (quitter) n.delete(documentId); else n.add(documentId);
        return n;
      });
    } finally {
      setEnCours(null);
    }
  };

  /** Le geste complet : la carte sort, puis le signalement est quitté pour de
   *  bon. */
  const quitterEnGlissant = (nature: NatureSignalement, documentId: string) => {
    if (glissante) return;
    setGlissante(documentId);
    minuteurs.current.push(window.setTimeout(() => {
      setGlissante(null);
      setNouvellePromue(true);
      void basculerQuittee(nature, documentId, true);
      /* Le temps que l'entrée se joue — après quoi la classe est retirée,
         sinon la dernière carte rejouerait l'animation à chaque rendu. */
      minuteurs.current.push(window.setTimeout(() => setNouvellePromue(false), DUREE_ENTREE));
    }, DUREE_SORTIE));
  };

  /* ── LE FLUX : DEUX REGISTRES, UNE COLONNE ───────────────────────────────
     Chaque entrée porte sa `nature`, son étiquette, ses pastilles et ses
     rubriques. ⚠️ Les RUBRIQUES diffèrent d'un registre à l'autre et ne sont
     pas alignées de force : une réclamation n'a pas de « pourquoi c'est
     arrivé », et en afficher le titre vide ferait passer un dossier normal
     pour un dossier bâclé. */
  type Entree = SignalementLisible & {
    nature: NatureSignalement;
    ref: string; titre: string; statut: string; gravite: string;
    contexte: string;
    etapes: [string, string][];
  };

  const entreesNc: Entree[] = ncs
    .filter((n) => !['soumise', 'annulee'].includes(String(n.statut)) && !(n as { sandbox?: boolean }).sandbox)
    .map((n) => ({
      nature: 'nc', documentId: n.documentId, ref: n.ref, titre: n.titre,
      dateDeclaration: n.dateDeclaration, dateDetection: n.dateDetection,
      auteur: n.detectePar || '',
      statut: vocabMeta(NC_STATES, String(n.statut)).label,
      gravite: String(n.gravite || ''), contexte: String(n.processus || ''),
      etapes: [
        ['Ce qui s’est passé', n.description || ''],
        ['Corrigé tout de suite', n.correction || ''],
        ['Pourquoi c’est arrivé', n.causes || ''],
        ['Ce qui change', n.actionCorrective || ''],
      ],
    }));

  /* ⚠️ `voitRecl` ET NON `voitNc` : sans la section `recl`, aucune
     réclamation n'entre dans le flux — même si le magasin en a chargé (il
     peut les tenir pour un autre écran). Le droit se relit ICI, au point
     d'affichage, et pas seulement au point de requête. */
  const entreesRecl: Entree[] = !voitRecl ? [] : recls.map((r) => ({
    nature: 'recl', documentId: r.documentId, ref: r.ref, titre: r.objet,
    dateReception: r.dateReception,
    statut: vocabMeta(REC_STATES, String(r.statut)).label,
    gravite: String(r.gravite || ''),
    /* LA PROVENANCE (« Patient », « Prescripteur », « Clinique / hôpital »), et
       JAMAIS le nom du plaignant. Savoir d'où vient la plainte enseigne quelque
       chose — un patient et un prescripteur ne reprochent pas les mêmes choses ;
       nommer la personne la jetterait sur le tableau de bord de tout le
       laboratoire, et le registre existe pour ça.
       ⚠️ Le LIBELLÉ et non la clé : `r.canal` vaut « prescripteur », ce qui
       s'affiche en minuscule au milieu d'une phrase soignée. Même table que le
       registre (`REC_CANAUX`), pour que les deux écrans disent le même mot. */
    contexte: REC_CANAUX.find((c) => c.key === String(r.canal || ''))?.label || '',
    etapes: [
      ['Ce qui nous est reproché', r.description || ''],
      ['Ce qu’on a répondu', r.reponse || ''],
    ],
  }));

  const candidates = fondre(entreesNc, entreesRecl);

  const aujourdhui = aujourdhuiIso();
  /* Ce que J'AI RÉDIGÉ compte comme lu (RQ, 19/10/2026) : jamais « nouveau »,
     masqué avec les quittés, et revu par le même bouton (`lusPourMoi`). */
  const lus = lusPourMoi(candidates, quittees || new Set<string>(), session?.key || '');
  const vue = pourLeTableauDeBord(candidates, voirQuittees ? new Set<string>() : lus, aujourdhui, NB_CARTES);
  const recentes = vue.affiches;

  const court = (t: string, n = 160) => { const x = String(t || '').replace(/\s+/g, ' ').trim(); return x.length > n ? `${x.slice(0, n - 1)}…` : x; };
  /* `key` posée ici : les rubriques sont désormais PARCOURUES (elles diffèrent
     d'un registre à l'autre), et une liste sans clé fait rejouer le DOM à
     chaque rendu. Le titre suffit — deux rubriques d'un même dossier ne
     portent pas le même. */
  const etape = (titre: string, texte: string) => (texte.trim() ? (
    <div key={titre} style={{ display: 'grid', gap: 1, fontSize: 12.5, lineHeight: 1.45 }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{titre}</span>
      <span style={{ color: 'var(--text-body)', overflowWrap: 'anywhere' }}>{court(texte)}</span>
    </div>
  ) : null);

  return (
    <section style={{ ...card, overflow: 'hidden' }}>
      <style>{CSS_GLISSE_NC}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
        <I n="alert" s={15} style={{ color: 'var(--color-primary)' }} />
        {/* Le titre SUIT ce que la colonne contient réellement : sans la
            section `recl`, elle ne montre que des NC, et promettre
            « signalements » l'aurait fait paraître incomplète. */}
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>
          {voitRecl ? 'Derniers signalements' : 'Dernières non-conformités'}
        </h2>
        <Bouton taille="sm" onClick={() => go('nc')}>Toutes les NC →</Bouton>
        {voitRecl && <Bouton taille="sm" onClick={() => go('recl')}>Réclamations →</Bouton>}
      </div>
      <p style={{ margin: 0, padding: '10px 18px 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {voitRecl
          ? 'Une non-conformité ou une réclamation n’est pas une faute : c’est un écart qu’on signale pour que le laboratoire apprenne. Voici les plus récentes, et ce qu’elles changent.'
          : 'Une non-conformité n’est pas une faute : c’est un écart qu’on signale pour que le laboratoire apprenne. Voici les plus récentes, et ce qu’elles changent.'}
      </p>
      {/* ⚠️ PLUS DE BANDEAU « N NC sans aucune action au plan » (RQ, 14/10/2026 :
          « ça ne me dérange pas les NC sans plan d'action ») : une NC peut se
          solder sans action — la correction immédiate suffit souvent. */}
      <div style={{ padding: '6px 0 4px' }}>
        {/* ⚠️ `quittees === null` COMPTE DANS L'ATTENTE : sans lui, le premier
            rendu montrerait les NC déjà lues, qui disparaîtraient ensuite sous
            les yeux — et un bloc vide passerait une fraction de seconde pour
            « aucune non-conformité ». */}
        {!pret || quittees === null ? <Skeleton rows={3} />
          : !recentes.length ? (
            /* ⚠️ DEUX VIDES À NE JAMAIS CONFONDRE (le piège des faux vides) :
               « il n'y en a aucun » est une AFFIRMATION sur le laboratoire ;
               « vous les avez tous lus » parle de l'écran. Dire la première
               à la place de la seconde ferait croire qu'il ne s'est rien passé
               — exactement l'inverse de ce que ce bloc existe pour montrer. */
            vue.quittesMasques > 0 ? (
              <EmptyState titre={voitRecl ? 'Vous avez lu tous les signalements récents.' : 'Vous avez lu toutes les non-conformités récentes.'}
                indice={`${vue.quittesMasques} déjà lu(s) — ils restent consultables dans leur registre, et le bouton ci-dessous les remet ici.`} />
            ) : <EmptyState titre={voitRecl ? 'Aucune non-conformité ni réclamation enregistrée.' : 'Aucune non-conformité enregistrée.'} />
          )
            : recentes.map((n, i) => {
              const g = graviteMeta(n.gravite);
              const neuve = vue.nouveaux.has(n.documentId);
              const dejaQuittee = !!quittees?.has(n.documentId);
              /* Le mien : lu d'office, il n'y a rien à quitter ni à remettre. */
              const redigeParMoi = !!n.auteur && n.auteur === session?.key;
              /* ⚠️ LA LIGNE N'EST PLUS UN SEUL BOUTON (05/10/2026) : « J'ai lu »
                 est un bouton, et un bouton dans un bouton n'est pas du HTML
                 valide — le navigateur défait l'imbrication, et le clic part au
                 hasard de l'un ou de l'autre. L'en-tête devient donc une ligne
                 ordinaire, et seul le CORPS (titre et étapes) ouvre la fiche. */
              /* La nouvelle venue est TOUJOURS la dernière de la fenêtre : on
                 a retiré une carte au-dessus d'elle dans une liste triée, et
                 c'est la suivante du tri qui entre par le bas. */
              const entrante = nouvellePromue && recentes.length === NB_CARTES && i === NB_CARTES - 1;
              return (
                <div key={n.documentId}
                  className={`db-nc${glissante === n.documentId ? ' db-nc-sort' : ''}${entrante ? ' db-nc-entre' : ''}`}
                  style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 18px', display: 'grid', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {neuve && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em', borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--cyan-600)', color: 'var(--text-on-primary)' }}
                        title={`Enregistré il y a moins de ${JOURS_NOUVEAUTE} jours et pas encore lu par vous`}>
                        NOUVEAU
                      </span>
                    )}
                    {/* ── D'OÙ ÇA VIENT, EN UN MOT ──────────────────────────
                        ⚠️ La provenance est ÉCRITE, elle n'est pas devinable :
                        deux registres dans une même colonne, et une
                        réclamation de patient ne se traite pas comme un écart
                        interne. Montrée même quand la colonne n'a que des NC :
                        une étiquette qui apparaît et disparaît selon le
                        contenu s'apprend moins bien qu'une étiquette constante. */}
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', borderRadius: 'var(--radius-pill)', padding: '2px 8px', border: '1px solid var(--border-strong)', color: 'var(--text-muted)' }}>
                      {LIBELLE_NATURE[n.nature]}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(dateDuSignalement(n))}</span>
                    {n.contexte && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {n.contexte}</span>}
                    <span style={{ marginInlineStart: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {n.gravite && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: g.bg, color: g.fg }}>{g.label}</span>}
                      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--surface-sunken)', color: 'var(--text-body)' }}>{n.statut}</span>
                      {/* ⚠️ Le libellé dit L'ACTE (« j'ai lu »), pas la
                          conséquence technique. Il ne vaut ni accusé de
                          réception ni prise en charge : l'infobulle le dit,
                          parce qu'un bouton près d'un signalement peut le
                          faire croire. */}
                      {redigeParMoi ? (
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}
                          title="Vous avez rédigé ce signalement : il compte comme déjà lu.">Rédigé par vous</span>
                      ) : <Bouton taille="sm" variante="discret" chargement={enCours === n.documentId}
                        aria-label={dejaQuittee ? `Remettre ${n.ref} dans la liste` : `J’ai lu ${n.ref} — le retirer de ma liste`}
                        title={dejaQuittee
                          ? 'Remettre ce signalement dans votre liste.'
                          : 'Le retirer de VOTRE liste seulement. Ne vaut ni accusé de réception, ni prise en charge : rien ne change sur le dossier.'}
                        onClick={() => (dejaQuittee
                          ? void basculerQuittee(n.nature, n.documentId, false)
                          : quitterEnGlissant(n.nature, n.documentId))}>
                        {dejaQuittee ? 'Remettre' : 'J’ai lu'}
                      </Bouton>}
                    </span>
                  </div>
                  <button onClick={() => ouvrirPanneau({ type: n.nature, valeur: n.documentId, origine: 'Tableau de bord' })}
                    className="ged-row" style={{ display: 'grid', gap: 6, width: '100%', textAlign: 'start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{n.titre}</span>
                    {n.etapes.map(([titre, texte]) => etape(titre, texte))}
                  </button>
                </div>
              );
            })}
      </div>

      {/* ── CE QUI EST MASQUÉ SE DIT, ET SE REVOIT ───────────────────────────
          ⚠️ Un filtre qui cache sans l'annoncer fait lire un écran incomplet
          comme un écran complet. Le compte est donc toujours écrit, et le
          retour est à un clic — « quitter » ne doit jamais être une perte. */}
      {quittees !== null && vue.quittesMasques > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 18px', borderTop: '1px solid var(--border-subtle)', background: 'var(--gray-25)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {vue.quittesMasques} {voitRecl ? 'signalement(s)' : 'non-conformité(s)'} que vous avez déjà lu(s){voirQuittees ? ' — affiché(s)' : ' — masqué(s) ici'}.
          </span>
          <Bouton taille="sm" variante="discret" onClick={() => setVoirQuittees((v) => !v)}
            aria-expanded={voirQuittees}>
            {voirQuittees ? 'Masquer ceux que j’ai lus' : 'Revoir ceux que j’ai lus'}
          </Bouton>
        </div>
      )}
    </section>
  );
}

export function Dashboard() {
  const { docs, ncs, actions, recls, audits, settings, session, ouvrirPanneau, openDoc, loading, booted, go, setFilter } = useGed();
  // Tant que le premier chargement n'a pas rendu, « aucune revue » serait faux.
  const enChargement = !booted || (loading && docs.length === 0);

  const soon = new Date(); soon.setDate(soon.getDate() + (settings?.notif?.revueAvant ?? 30) * 2);
  // Une revue ÉCHUE ne doit jamais devenir invisible : un document déjà passé
  // « en révision » était exclu du filtre (constat QA MIN-5), si bien qu'une
  // révision qui traîne laissait afficher « Aucune revue proche » alors qu'une
  // revue était en retard. On les inclut, en les distinguant à l'affichage.
  const revisions = docs
    .filter((d) => (d.statut === 'envigueur' || d.statut === 'enrevision')
      && d.prochaineRevue && new Date(d.prochaineRevue) <= soon)
    .sort((a, b) => String(a.prochaineRevue).localeCompare(String(b.prochaineRevue)));
  // Le constat « NC sans action au plan » (relevé MAN-10) vit désormais dans
  // `DernieresNC`, réservé au RQ et aux responsables qui gèrent le plan.
  const withdrawn = docs.filter((d) => d.statut === 'retire');

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <TitrePage eyebrow="Pilotage qualité" titre="Tableau de bord" />

      {/* Le mémo du jour, EN PREMIER : c'est la question qu'on se pose en
          arrivant, avant les chiffres et même avant les accès rapides. Il ne
          rend rien si l'appelant n'a pas de planning — un agrément muet plutôt
          qu'un cadre vide. */}
      <MonJour />

      {/* ⚠️ APRÈS le mémo du jour, AVANT les accès rapides. Ce n'est pas une
          tâche à faire aujourd'hui — le mémo garde sa place — mais c'est une
          décision qui dort : plus bas, entre les chiffres, elle ne serait
          jamais lue, puisque son objet est justement une rubrique qu'on ne
          voit pas. Ne rend rien s'il n'y a rien, et rien pour qui n'est pas RQ. */}
      <RappelsFermeture />

      {/* Accès rapides — AVANT les indicateurs : on vient ici d'abord pour
          ALLER quelque part (lire un document, saisir le CQI, pointer une
          maintenance), les chiffres se lisent ensuite. */}
      <AccesRapides />

      {/* Les cinq chiffres du fonds documentaire sont dans l'écran Documents
          depuis le 04/10/2026 (RQ : « de faible intérêt » ici, en tête). */}

      {/* DEUX COLONNES (RQ, 04/10/2026) : le travail à gauche (≈ 2/3), les
          dernières non-conformités à droite (≈ 1/3),
          défilant avec la page. Sur un écran étroit, `ged-2col` les empile. */}
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'grid', gap: 20, minWidth: 0 }}>

      {withdrawn.length > 0 && (
        <section style={{ ...card, borderColor: 'var(--red-200)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', background: 'var(--red-50)', borderBottom: '1px solid var(--border-default)', color: 'var(--red-600)', fontWeight: 700, fontSize: 14 }}>
            <I n="alert" s={16} />Retraits d’urgence actifs
          </div>
          {withdrawn.map((d) => (
            <button key={d.documentId} onClick={() => openDoc(d.code)} className="ged-row" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 18px', cursor: 'pointer' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)' }}>{d.code}</span>
              <span style={{ flex: 1, fontSize: 13.5, color: 'var(--text-strong)', fontWeight: 600 }}>{d.titre}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>{d.retraitMotif}</span>
            </button>
          ))}
        </section>
      )}

      <LabCalendar />

      <DemandesATraiter />

      <div>
        <section style={{ ...card, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
            <I n="clock" s={15} style={{ color: 'var(--color-primary)' }} />
            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Révisions à échéance</h2>
          </div>
          {enChargement ? <Skeleton rows={2} /> : revisions.length === 0 && <EmptyState titre="Aucune revue proche." />}
          {revisions.map((d) => {
            const overdue = new Date(String(d.prochaineRevue)) < new Date();
            return (
              <button key={d.documentId} onClick={() => openDoc(d.code)} className="ged-row" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start', background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 18px', cursor: 'pointer' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{d.code}</span>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.titre}</span>
                {d.statut === 'enrevision' && (
                  <span title="Une révision est déjà en cours pour ce document" style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--amber-50)', color: 'var(--amber-600)', whiteSpace: 'nowrap' }}>en révision</span>
                )}
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: overdue ? 'var(--red-600)' : 'var(--amber-600)' }}>{fmtDate(d.prochaineRevue)}</span>
              </button>
            );
          })}
        </section>
      </div>

      <section style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
          <I n="doc" s={15} style={{ color: 'var(--color-primary)' }} />
          <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Ma vue — {session.name}</h2>
        </div>
        {(() => {
          /** Une entrée du panneau, quel que soit l'objet qu'elle désigne. */
          type Tache = { id: string; ref: string; titre: string; pill: React.ReactNode; retard?: boolean; ouvrir: () => void };
          const dPill = (statut: string) => <StatePill statut={statut} />;
          const smqPill = (m: { label: string; bg: string; fg: string }) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{m.label}</span>
          );

          // Une tâche s'ouvre EN PANNEAU : on la traite et on retrouve son
          // panneau derrière, au lieu d'être déposé sur un registre et de
          // devoir revenir au tableau de bord entre chaque dossier.
          /* `vue` : ce que la fiche doit montrer en arrivant. C'est L'EXCEPTION
             demandée par la RQ le 06/10/2026 — une fiche ouverte depuis le
             catalogue montre la version APPLICABLE et une mention vers le
             chantier ; une fiche ouverte depuis « à rédiger / à vérifier / à
             approuver » montre le CHANTIER, puisque c'est lui qu'on vient
             traiter. « Documents à lire » n'en fait pas partie : on accuse
             réception de ce qui s'applique, jamais d'un brouillon. */
          type Vue = { onglet?: 'fichier' | 'lecture'; voirEnCours?: boolean };
          const doc = (liste: typeof docs, vue?: Vue): Tache[] => liste.map((d) => ({
            id: d.documentId, ref: d.code, titre: d.titre, pill: dPill(d.statut),
            ouvrir: () => openDoc(d.code, vue?.onglet, vue?.voirEnCours),
          }));

          // Désigné pour signer MAIS empêché par la séparation des tâches : montré,
          // marqué « bloqué », jamais caché (voir `signatureBloquee`).
          const docBloques = (etape: 'verification' | 'approbation'): Tache[] => docs
            .filter((d) => signatureBloquee(d, session) === etape)
            .map((d) => ({
              id: d.documentId, ref: d.code, titre: d.titre,
              pill: (
                <span title={etape === 'verification'
                  ? 'Vous avez contribué à ce document : la séparation des tâches vous interdit de le vérifier. Désignez un autre vérificateur.'
                  : 'Vous avez rédigé ou vérifié ce document : la séparation des tâches vous interdit de l’approuver. Désignez un autre approbateur.'}
                  style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--amber-50)', color: 'var(--amber-600)', fontSize: 11.5, fontWeight: 700 }}>
                  Bloqué — autre signataire requis
                </span>
              ),
              // Bloqué par la séparation des tâches, mais c'est bien le chantier
              // qu'il vient regarder : la fiche l'ouvre dessus.
              ouvrir: () => openDoc(d.code, undefined, true),
            }));

          // Les objets SMQ : uniquement les MIENS et uniquement ceux qui ne sont
          // pas soldés — un panneau qui liste des dossiers clos ne vaut pas
          // mieux qu'un panneau vide.
          /* LES DEMANDES D'OUVERTURE DE NC (RQ, 08/10/2026 : « je voudrais voir
             dans mes demandes à traiter les demandes d'ouverture de NC, cela
             concerne aussi le CQ »). Une déclaration « soumise » n'a pas de
             responsable : elle n'entrait donc jamais dans « mes NC à traiter ».
             Elle est due à qui peut la TRANCHER — le RQ et le CQ, même cercle
             que `TriageNC` — et à personne d'autre : une tâche qu'on ne peut
             pas exécuter est un reproche, pas une tâche. */
          const aTrancher: Tache[] = !peutTrancherNC(session.roles, session.actes) ? [] : ncs.filter(ncSoumise)
            .sort((a, b) => String(a.dateDeclaration).localeCompare(String(b.dateDeclaration)))
            .map((n) => ({
              id: n.documentId, ref: n.ref, titre: n.titre, pill: smqPill(vocabMeta(NC_STATES, n.statut)),
              ouvrir: () => ouvrirPanneau({ type: 'nc', valeur: n.documentId, origine: 'Mes tâches' }),
            }));
          const mesNcs: Tache[] = ncs.filter((n) => n.responsable === session.key && ncOuverte(n)).map((n) => ({
            id: n.documentId, ref: n.ref, titre: n.titre, pill: smqPill(vocabMeta(NC_STATES, n.statut)),
            ouvrir: () => ouvrirPanneau({ type: 'nc', valeur: n.documentId, origine: 'Mes tâches' }),
          }));
          const mesActions: Tache[] = actions.filter((a) => actionMienne(a, session.key) && actionOuverte(a)).map((a) => ({
            id: a.documentId, ref: a.ref, titre: a.titre, pill: smqPill(vocabMeta(ACTION_STATES, a.statut)), retard: !!isLate(a),
            ouvrir: () => ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: 'Mes tâches' }),
          }));
          const mesRecls: Tache[] = recls.filter((r) => r.responsable === session.key && reclOuverte(r)).map((r) => ({
            id: r.documentId, ref: r.ref, titre: r.objet, pill: smqPill(vocabMeta(REC_STATES, r.statut)),
            ouvrir: () => ouvrirPanneau({ type: 'recl', valeur: r.documentId, origine: 'Mes tâches' }),
          }));
          const mesAudits: Tache[] = audits.filter((a) => a.auditeur === session.key && auditOuvert(a)).map((a) => ({
            id: a.documentId, ref: a.ref, titre: a.intitule, pill: smqPill(vocabMeta(AUDIT_STATES, a.statut)), retard: auditEnRetard(a),
            ouvrir: () => ouvrirPanneau({ type: 'audit', valeur: a.documentId, origine: 'Mes tâches' }),
          }));

          /* « Voir les N » (RQ, 04/10/2026) : au-delà de 7 entrées, la rubrique
             s'arrête et mène à l'écran complet — Documents FILTRÉ « À moi
             d'agir — <étape> » pour les documents (même règle :
             lib/mes-documents.ts), le registre pour le reste. */
          const versDocs = (etape: EtapeMoi) => () => {
            (['fQuery', 'fType', 'fDisc', 'fProc', 'fStatut'] as const).forEach((k) => setFilter(k, ''));
            setFilter('fMoi', `amoi:${etape}`);
            go('list');
          };
          const groups: { label: string; items: Tache[]; toujours?: boolean; vide?: string; voirTout?: () => void }[] = [
            // « À rédiger » ouvre le FICHIER : c'est là que le texte se travaille.
            { label: 'À rédiger', items: doc(docs.filter((d) => etapesAMoi(d, session).includes('rediger')), { onglet: 'fichier', voirEnCours: true }), voirTout: versDocs('rediger') },
            /* « À vérifier » / « À approuver » : la règle du serveur (taches-signature.ts) — jamais ce qu'on a rédigé,
               le signataire désigné seul, le rôle ne suffit que si personne n'est désigné. */
            { label: 'À vérifier', items: [...doc(docs.filter((d) => attendDeMoi(d, session) === 'verification'), { voirEnCours: true }), ...docBloques('verification')], voirTout: versDocs('verifier') },
            { label: 'À approuver', items: [...doc(docs.filter((d) => attendDeMoi(d, session) === 'approbation'), { voirEnCours: true }), ...docBloques('approbation')], voirTout: versDocs('approuver') },
            /* TOUJOURS AFFICHÉE, même vide (RQ, 04/10/2026) : « les documents
               que je dois lire » est la question que chacun se pose ; une
               rubrique qui disparaît quand elle est vide laisse croire qu'elle
               n'existe pas. */
            { label: 'Documents à lire — accusé de lecture attendu', items: doc(docs.filter((d) => (d.diffusion || []).some((x) => x.userId === session.key && x.requis && !x.dateAck))), toujours: true, vide: 'Aucun document à lire : rien ne vous a été diffusé qui attende votre accusé de lecture.', voirTout: versDocs('lire') },
            { label: 'Déclarations de NC à retenir ou rejeter', items: aTrancher, voirTout: () => go('nc') },
            { label: 'Mes non-conformités à traiter', items: mesNcs, voirTout: () => go('nc') },
            { label: 'Mes actions à mener', items: mesActions, voirTout: () => go('actions') },
            { label: 'Mes réclamations à traiter', items: mesRecls, voirTout: () => go('recl') },
            /* ⚠️ « À mener » était FAUX pour la moitié du lot (relevé MAJ-07) :
               `auditOuvert` retient aussi les audits RÉALISÉS, qui attendent
               leur clôture — mener un audit déjà réalisé ne veut rien dire. Le
               registre des audits, lui, nommait déjà correctement le même
               ensemble « À clôturer (2) ». Deux libellés pour un même ensemble,
               dont un faux : on garde celui qui est vrai. */
            { label: 'Mes audits à mener ou à clôturer', items: mesAudits, voirTout: () => go('audits') },
          ].filter((g) => g.items.length || g.toujours);
          if (!groups.length) return <EmptyState titre="Rien à traiter." />;
          return groups.map((g) => (
            <div key={g.label} style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 6 }}>{g.label} · {g.items.length}</div>
              {!g.items.length && g.vide && (
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{g.vide}</div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {g.items.slice(0, MAX_MA_VUE).map((t) => (
                  <button key={t.id} onClick={t.ouvrir} title={`${t.ref} — ${t.titre}`} className="ged-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--surface-sunken)', border: `1px solid ${t.retard ? 'var(--red-300)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-md)', padding: '6px 11px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-primary-active)' }}>{t.ref}</span>
                    {t.pill}
                    {t.retard && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>en retard</span>}
                  </button>
                ))}
                {g.items.length > MAX_MA_VUE && g.voirTout && (
                  <Bouton variante="discret" taille="sm" onClick={g.voirTout}>
                    Voir les {g.items.length} →
                  </Bouton>
                )}
              </div>
            </div>
          ));
        })()}
      </section>
      </div>

      {/* ── LA COLONNE DE DROITE : CE QU'ON LIT, PAS CE QU'ON FAIT ──────
          Les signalements d'abord — c'est ce qui concerne le laboratoire —
          puis l'expresso, qui concerne le métier.
          ⚠️ L'EXPRESSO EST EN DERNIER, ET DÉLIBÉRÉMENT : c'est un agrément.
          Au-dessus des non-conformités, il aurait fait passer une curiosité
          devant un écart du laboratoire — et c'est l'ordre, pas le libellé,
          qui dit ce qui compte. */}
      <aside style={{ minWidth: 0, display: 'grid', gap: 20, alignContent: 'start' }}>
        <DerniersSignalements />
        <Expresso />
      </aside>
      </div>
    </div>
  );
}
